import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useAuth, getIdToken } from '@pooflabs/web';
import {
  subscribeManyTeamMembers,
  setTeamMembers,
  deleteTeamMembers,
  TeamMembersResponse,
} from '@/lib/collections/teamMembers';
import { setFile, getFiles } from '@/lib/db-client';
import { useRealtimeData } from '@/hooks/use-realtime-data';
import { subscribeManyMedia, MediaResponse, setMedia, deleteMedia, updateMedia } from '@/lib/collections/media';
import { ADMIN_ADDRESS, FOUNDER_ADDRESS } from '@/lib/constants';
import {
  subscribeManyArtistBio,
  ArtistBioResponse,
  setArtistBio,
  updateArtistBio,
} from '@/lib/collections/artistBio';
import {
  subscribeManyWipPosts,
  WipPostsResponse,
  setWipPosts,
  deleteWipPosts,
} from '@/lib/collections/wipPosts';
import {
  subscribeManyLuckyDrawEntries,
  LuckyDrawEntriesResponse,
  updateLuckyDrawEntries,
} from '@/lib/collections/luckyDrawEntries';
import {
  getLegalPages,
  setLegalPages,
  LegalPagesResponse,
} from '@/lib/collections/legalPages';
import { Time } from '@/lib/db-client';
import {
  subscribeManyLuckyDrawPrizes,
  LuckyDrawPrizesResponse as _LuckyDrawPrizesResponse,
  updateLuckyDrawPrizes,
} from '@/lib/collections/luckyDrawPrizes';

// imageUrl is written to the DB but not yet in the generated type
type LuckyDrawPrizesResponse = _LuckyDrawPrizesResponse & {
  imageUrl?: string;
};

import { createAuthenticatedApiClient } from '@/lib/api-client';
import { PageLayout } from '@/components/poof-ui';
import { toast } from 'sonner';
import { playClick } from '@/utils/sounds';
import {
  Trash2,
  Wallet,
  ShieldAlert,
  ShieldCheck,
  Terminal,
  Image,
  Video,
  Plus,
  Pencil,
  X,
  Check,
  Film,
  Upload,
  Link,
  Palette,
  Music,
  Trophy,
  RotateCcw,
  Copy,
  ChevronDown,
  ChevronUp,
  FileText,
  Star,
  Calendar,
  ToggleLeft,
  ToggleRight,
  Users,
} from 'lucide-react';
import {
  subscribeManyFanArtSubmissions,
  setFanArtSubmissions,
  updateFanArtSubmissions,
  deleteFanArtSubmissions,
  FanArtSubmissionsResponse,
} from '@/lib/collections/fanArtSubmissions';
import {
  subscribeLuckyDrawCampaigns,
  LuckyDrawCampaignsResponse,
  setLuckyDrawCampaigns,
  updateLuckyDrawCampaigns,
} from '@/lib/collections/luckyDrawCampaigns';

type LuckyDrawEntriesResponseExtended = LuckyDrawEntriesResponse & {
  adminStatus?: string;
  adminRemark?: string;
};

// ─── Auth guard ──────────────────────────────────────────────────────────────

const AccessDenied: React.FC<{ login: () => void; connected: boolean }> = ({
  login,
  connected,
}) => (
  <div
    className="min-h-screen flex items-center justify-center"
    style={{ background: 'hsl(var(--background))' }}
  >
    <div className="flex flex-col items-center gap-6 text-center max-w-sm px-6">
      {connected ? (
        <>
          <div
            className="w-16 h-16 rounded-full flex items-center justify-center"
            style={{ background: 'rgba(230,57,70,0.15)', border: '1px solid rgba(230,57,70,0.4)' }}
          >
            <ShieldAlert size={32} style={{ color: '#E63946' }} />
          </div>
          <div>
            <p
              className="text-2xl font-bold uppercase mb-2"
              style={{ fontFamily: "'Bangers', cursive", letterSpacing: '0.1em', color: '#E63946' }}
            >
              Access Denied
            </p>
            <p className="text-sm" style={{ color: 'rgba(255,255,255,0.5)' }}>
              This wallet is not authorized to access the admin panel.
            </p>
          </div>
        </>
      ) : (
        <>
          <div
            className="w-16 h-16 rounded-full flex items-center justify-center"
            style={{ background: 'rgba(124,252,0,0.1)', border: '1px solid rgba(124,252,0,0.3)' }}
          >
            <Wallet size={32} style={{ color: '#7CFC00' }} />
          </div>
          <div>
            <p
              className="text-2xl font-bold uppercase mb-2"
              style={{ fontFamily: "'Bangers', cursive", letterSpacing: '0.1em', color: '#7CFC00' }}
            >
              Admin Access
            </p>
            <p className="text-sm mb-4" style={{ color: 'rgba(255,255,255,0.5)' }}>
              Connect your admin wallet to access the admin panel.
            </p>
          </div>
          <button
            onClick={login}
            className="px-6 py-3 font-bold uppercase text-sm transition-all duration-200"
            style={{
              fontFamily: "'Bangers', cursive",
              letterSpacing: '0.12em',
              background: 'rgba(124,252,0,0.15)',
              border: '1px solid rgba(124,252,0,0.5)',
              color: '#7CFC00',
              borderRadius: '0.5rem',
            }}
            onMouseEnter={e => {
              (e.currentTarget as HTMLButtonElement).style.background = 'rgba(124,252,0,0.25)';
              (e.currentTarget as HTMLButtonElement).style.borderColor = '#7CFC00';
            }}
            onMouseLeave={e => {
              (e.currentTarget as HTMLButtonElement).style.background = 'rgba(124,252,0,0.15)';
              (e.currentTarget as HTMLButtonElement).style.borderColor = 'rgba(124,252,0,0.5)';
            }}
          >
            Connect Wallet
          </button>
        </>
      )}
    </div>
  </div>
);

// ─── Media Form ───────────────────────────────────────────────────────────────

interface MediaFormData {
  url: string;
  title: string;
  order: string;
}

interface MediaFormProps {
  type: 'image' | 'video';
  initial?: MediaFormData;
  onSubmit: (data: MediaFormData) => void;
  onCancel: () => void;
  submitting: boolean;
}

const inputStyle = {
  background: 'rgba(255,255,255,0.05)',
  border: '1px solid rgba(255,255,255,0.12)',
  color: 'rgba(255,255,255,0.9)',
  borderRadius: '0.5rem',
  padding: '0.5rem 0.75rem',
  width: '100%',
  fontSize: '0.875rem',
  fontFamily: 'monospace',
  outline: 'none',
} as const;

// ─── Video Form (URL + File Upload) ───────────────────────────────────────────

interface VideoFormProps {
  initial?: MediaFormData;
  onSubmit: (data: MediaFormData) => void;
  onCancel: () => void;
  submitting: boolean;
}

const VideoForm: React.FC<VideoFormProps> = ({ initial, onSubmit, onCancel, submitting }) => {
  const [mode, setMode] = useState<'url' | 'upload'>('url');
  const [url, setUrl] = useState(initial?.url ?? '');
  const [title, setTitle] = useState(initial?.title ?? '');
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<string | null>(null);
  const [uploadedUrl, setUploadedUrl] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const finalUrl = mode === 'upload' ? (uploadedUrl ?? '') : url;
    if (!finalUrl.trim()) {
      toast.error(mode === 'upload' ? 'Please upload a video file first' : 'URL is required');
      return;
    }
    onSubmit({ url: finalUrl.trim(), title: title.trim(), order: '0' });
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const maxSizeMb = 500;
    if (file.size > maxSizeMb * 1024 * 1024) {
      toast.error(`File too large. Maximum size is ${maxSizeMb}MB.`);
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    setUploading(true);
    setUploadedUrl(null);
    setUploadProgress(`Uploading ${file.name}…`);

    try {
      const fileId = `${crypto.randomUUID()}-${file.name.replace(/[^a-zA-Z0-9._-]/g, '_')}`;
      const path = `adminFiles/${fileId}`;
      const uploadOk = await setFile(path, file);
      if (!uploadOk) throw new Error('Upload returned false');
      const filesResult = await getFiles(path);
      const fileItem = (Array.isArray(filesResult) ? filesResult[0] : null) as { url?: string } | null;
      if (fileItem?.url) {
        setUploadedUrl(fileItem.url);
        setUploadProgress(null);
        toast.success('Video uploaded successfully');
      } else {
        throw new Error('No URL returned after upload');
      }
    } catch (err) {
      console.error('Upload failed', err);
      toast.error('Upload failed. Try again.');
      setUploadProgress(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
    } finally {
      setUploading(false);
    }
  };

  const isDisabled = submitting || uploading;

  const tabBtn = (active: boolean, onClick: () => void, icon: React.ReactNode, label: string) => (
    <button
      type="button"
      onClick={onClick}
      className="flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-bold uppercase transition-all"
      style={{
        fontFamily: "'Bangers', cursive",
        letterSpacing: '0.08em',
        background: active ? 'rgba(230,57,70,0.18)' : 'rgba(255,255,255,0.04)',
        border: active ? '1px solid rgba(230,57,70,0.5)' : '1px solid rgba(255,255,255,0.08)',
        color: active ? '#E63946' : 'rgba(255,255,255,0.4)',
        cursor: 'pointer',
      }}
    >
      {icon}
      {label}
    </button>
  );

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-3">
      {/* Mode toggle */}
      <div className="flex items-center gap-2">
        {tabBtn(mode === 'url', () => { setMode('url'); setUploadedUrl(null); }, <Link size={11} />, 'Paste URL')}
        {tabBtn(mode === 'upload', () => setMode('upload'), <Upload size={11} />, 'Upload File')}
      </div>

      {/* URL input */}
      {mode === 'url' && (
        <div>
          <label className="block text-xs uppercase tracking-widest mb-1.5" style={{ color: 'rgba(255,255,255,0.4)' }}>
            Video URL (YouTube embed or MP4)
          </label>
          <input
            style={inputStyle}
            value={url}
            onChange={e => setUrl(e.target.value)}
            placeholder="https://www.youtube.com/embed/..."
            disabled={isDisabled}
          />
        </div>
      )}

      {/* File upload */}
      {mode === 'upload' && (
        <div>
          <label className="block text-xs uppercase tracking-widest mb-1.5" style={{ color: 'rgba(255,255,255,0.4)' }}>
            Video File (MP4, WebM, MOV — max 500 MB)
          </label>

          {/* Drop zone */}
          <div
            className="relative flex flex-col items-center justify-center gap-3 rounded-lg transition-all"
            style={{
              border: uploadedUrl
                ? '1px solid rgba(124,252,0,0.4)'
                : '2px dashed rgba(230,57,70,0.3)',
              background: uploadedUrl
                ? 'rgba(124,252,0,0.04)'
                : 'rgba(230,57,70,0.04)',
              padding: '2rem 1rem',
              cursor: uploading ? 'default' : 'pointer',
              minHeight: 120,
            }}
            onClick={() => !uploading && !uploadedUrl && fileInputRef.current?.click()}
          >
            {uploading ? (
              <>
                <div
                  className="w-8 h-8 rounded-full border-2 animate-spin"
                  style={{ borderColor: '#E63946', borderTopColor: 'transparent' }}
                />
                <p className="text-xs font-mono" style={{ color: 'rgba(255,255,255,0.5)' }}>
                  {uploadProgress}
                </p>
              </>
            ) : uploadedUrl ? (
              <>
                <div
                  className="w-8 h-8 rounded-full flex items-center justify-center"
                  style={{ background: 'rgba(124,252,0,0.15)' }}
                >
                  <Check size={16} style={{ color: '#7CFC00' }} />
                </div>
                <p className="text-xs font-bold uppercase" style={{ fontFamily: "'Bangers', cursive", letterSpacing: '0.1em', color: '#7CFC00' }}>
                  Upload Complete
                </p>
                <p className="text-xs font-mono text-center break-all px-2" style={{ color: 'rgba(255,255,255,0.35)', maxWidth: 360 }}>
                  {uploadedUrl}
                </p>
                <button
                  type="button"
                  onClick={e => { e.stopPropagation(); setUploadedUrl(null); if (fileInputRef.current) fileInputRef.current.value = ''; }}
                  className="text-xs px-3 py-1 rounded transition-all"
                  style={{
                    background: 'rgba(255,255,255,0.06)',
                    border: '1px solid rgba(255,255,255,0.12)',
                    color: 'rgba(255,255,255,0.5)',
                    cursor: 'pointer',
                    fontFamily: 'monospace',
                  }}
                >
                  Replace file
                </button>
              </>
            ) : (
              <>
                <div
                  className="w-10 h-10 rounded-lg flex items-center justify-center"
                  style={{ background: 'rgba(230,57,70,0.12)', border: '1px solid rgba(230,57,70,0.25)' }}
                >
                  <Upload size={20} style={{ color: '#E63946' }} />
                </div>
                <div className="text-center">
                  <p className="text-sm font-bold uppercase mb-1" style={{ fontFamily: "'Bangers', cursive", letterSpacing: '0.1em', color: 'rgba(255,255,255,0.7)' }}>
                    Click to select a video
                  </p>
                  <p className="text-xs" style={{ color: 'rgba(255,255,255,0.3)' }}>
                    MP4, WebM, MOV · up to 500 MB
                  </p>
                </div>
              </>
            )}

            <input
              ref={fileInputRef}
              type="file"
              accept="video/*"
              onChange={handleFileChange}
              disabled={uploading}
              style={{ display: 'none' }}
            />
          </div>
        </div>
      )}

      {/* Title */}
      <div>
        <label className="block text-xs uppercase tracking-widest mb-1.5" style={{ color: 'rgba(255,255,255,0.4)' }}>
          Title (optional)
        </label>
        <input
          style={inputStyle}
          value={title}
          onChange={e => setTitle(e.target.value)}
          placeholder="Caption or title..."
          disabled={isDisabled}
        />
      </div>

      <div className="flex gap-2 mt-1">
        <button
          type="submit"
          disabled={isDisabled}
          className="flex items-center gap-1.5 px-4 py-2 rounded text-xs font-bold uppercase transition-all"
          style={{
            fontFamily: "'Bangers', cursive",
            letterSpacing: '0.1em',
            background: isDisabled ? 'rgba(124,252,0,0.08)' : 'rgba(124,252,0,0.15)',
            border: '1px solid rgba(124,252,0,0.4)',
            color: '#7CFC00',
            cursor: isDisabled ? 'not-allowed' : 'pointer',
          }}
        >
          <Check size={12} />
          {submitting ? 'Saving…' : 'Save'}
        </button>
        <button
          type="button"
          onClick={onCancel}
          disabled={isDisabled}
          className="flex items-center gap-1.5 px-4 py-2 rounded text-xs font-bold uppercase transition-all"
          style={{
            fontFamily: "'Bangers', cursive",
            letterSpacing: '0.1em',
            background: 'rgba(255,255,255,0.04)',
            border: '1px solid rgba(255,255,255,0.1)',
            color: 'rgba(255,255,255,0.5)',
            cursor: isDisabled ? 'not-allowed' : 'pointer',
          }}
        >
          <X size={12} />
          Cancel
        </button>
      </div>
    </form>
  );
};

// ─── Image / Generic Media Form ────────────────────────────────────────────────

const MediaForm: React.FC<MediaFormProps> = ({ type, initial, onSubmit, onCancel, submitting }) => {
  const [url, setUrl] = useState(initial?.url ?? '');
  const [title, setTitle] = useState(initial?.title ?? '');
  const [order, setOrder] = useState(initial?.order ?? '1');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!url.trim()) {
      toast.error('URL is required');
      return;
    }
    onSubmit({ url: url.trim(), title: title.trim(), order });
  };

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-3">
      <div>
        <label className="block text-xs uppercase tracking-widest mb-1.5" style={{ color: 'rgba(255,255,255,0.4)' }}>
          {type === 'video' ? 'Video URL (YouTube embed or MP4)' : 'Image URL'}
        </label>
        <input
          style={inputStyle}
          value={url}
          onChange={e => setUrl(e.target.value)}
          placeholder={type === 'video' ? 'https://www.youtube.com/embed/...' : 'https://...'}
        />
      </div>
      <div>
        <label className="block text-xs uppercase tracking-widest mb-1.5" style={{ color: 'rgba(255,255,255,0.4)' }}>
          Title (optional)
        </label>
        <input
          style={inputStyle}
          value={title}
          onChange={e => setTitle(e.target.value)}
          placeholder="Caption or title..."
        />
      </div>
      {type === 'image' && (
        <div>
          <label className="block text-xs uppercase tracking-widest mb-1.5" style={{ color: 'rgba(255,255,255,0.4)' }}>
            Order
          </label>
          <input
            style={{ ...inputStyle, width: '80px' }}
            type="number"
            min="1"
            value={order}
            onChange={e => setOrder(e.target.value)}
          />
        </div>
      )}
      <div className="flex gap-2 mt-1">
        <button
          type="submit"
          disabled={submitting}
          className="flex items-center gap-1.5 px-4 py-2 rounded text-xs font-bold uppercase transition-all"
          style={{
            fontFamily: "'Bangers', cursive",
            letterSpacing: '0.1em',
            background: submitting ? 'rgba(124,252,0,0.08)' : 'rgba(124,252,0,0.15)',
            border: '1px solid rgba(124,252,0,0.4)',
            color: '#7CFC00',
            cursor: submitting ? 'not-allowed' : 'pointer',
          }}
        >
          <Check size={12} />
          {submitting ? 'Saving…' : 'Save'}
        </button>
        <button
          type="button"
          onClick={onCancel}
          disabled={submitting}
          className="flex items-center gap-1.5 px-4 py-2 rounded text-xs font-bold uppercase transition-all"
          style={{
            fontFamily: "'Bangers', cursive",
            letterSpacing: '0.1em',
            background: 'rgba(255,255,255,0.04)',
            border: '1px solid rgba(255,255,255,0.1)',
            color: 'rgba(255,255,255,0.5)',
            cursor: 'pointer',
          }}
        >
          <X size={12} />
          Cancel
        </button>
      </div>
    </form>
  );
};

// ─── Main AdminPage ───────────────────────────────────────────────────────────

export const AdminPage: React.FC = () => {
  const { user, loading, login } = useAuth();
  // Media management state
  const [showAddImage, setShowAddImage] = useState(false);
  const [showAddVideo, setShowAddVideo] = useState(false);
  const [editingMediaId, setEditingMediaId] = useState<string | null>(null);
  const [mediaSaving, setMediaSaving] = useState(false);
  const [deletingMediaIds, setDeletingMediaIds] = useState<Set<string>>(new Set());

  // Hero background video state
  const [showAddHeroVideo, setShowAddHeroVideo] = useState(false);
  const [heroVideoSaving, setHeroVideoSaving] = useState(false);
  const [editingHeroVideoId, setEditingHeroVideoId] = useState<string | null>(null);

  // Audio/Song state
  const [showAddAudio, setShowAddAudio] = useState(false);
  const [audioSaving, setAudioSaving] = useState(false);
  const [audioUploading, setAudioUploading] = useState(false);
  const [audioUploadProgress, setAudioUploadProgress] = useState<string | null>(null);
  const [audioUploadedUrl, setAudioUploadedUrl] = useState<string | null>(null);
  const [audioTitle, setAudioTitle] = useState('');
  const audioFileRef = useRef<HTMLInputElement>(null);

  // Artist Bio state
  const [bioSaving, setBioSaving] = useState(false);
  const [showBioForm, setShowBioForm] = useState(false);
  const [bioForm, setBioForm] = useState({
    name: '',
    role: '',
    bio: '',
    imageUrl: '',
    videoUrl: '',
    xAccount: '',
  });
  const [bioImgUploading, setBioImgUploading] = useState(false);
  const [bioImgUploadedUrl, setBioImgUploadedUrl] = useState<string | null>(null);
  const bioImgRef = useRef<HTMLInputElement>(null);
  const [bioVideoMode, setBioVideoMode] = useState<'url' | 'upload'>('url');
  const [bioVideoUploading, setBioVideoUploading] = useState(false);
  const [bioVideoUploadedUrl, setBioVideoUploadedUrl] = useState<string | null>(null);
  const [bioVideoUploadProgress, setBioVideoUploadProgress] = useState<string | null>(null);
  const bioVideoRef = useRef<HTMLInputElement>(null);

  // WIP Posts state
  const [showAddWip, setShowAddWip] = useState(false);
  const [wipSaving, setWipSaving] = useState(false);
  const [deletingWipIds, setDeletingWipIds] = useState<Set<string>>(new Set());
  const [wipForm, setWipForm] = useState({
    title: '',
    description: '',
    mediaUrl: '',
    mediaType: 'image' as 'image' | 'video',
    order: '1',
  });
  const [wipMediaUploading, setWipMediaUploading] = useState(false);
  const [wipMediaUploadedUrl, setWipMediaUploadedUrl] = useState<string | null>(null);
  const wipMediaRef = useRef<HTMLInputElement>(null);

  // Team members state
  const [teamMemberWallet, setTeamMemberWallet] = useState('');
  const [addingTeamMember, setAddingTeamMember] = useState(false);
  const [removingTeamMember, setRemovingTeamMember] = useState<string | null>(null);

  // Legal pages state
  const [legalTab, setLegalTab] = useState<'terms' | 'privacy'>('terms');
  const [termsPage, setTermsPage] = useState<LegalPagesResponse | null>(null);
  const [privacyPage, setPrivacyPage] = useState<LegalPagesResponse | null>(null);
  const [termsContent, setTermsContent] = useState('');
  const [privacyContent, setPrivacyContent] = useState('');
  const [termsSaving, setTermsSaving] = useState(false);
  const [privacySaving, setPrivacySaving] = useState(false);
  const [legalLoaded, setLegalLoaded] = useState(false);

  const isAdmin =
    !!user?.address &&
    (user.address === ADMIN_ADDRESS || user.address === FOUNDER_ADDRESS);

  // Subscribe to all media entries
  const { data: mediaEntries } = useRealtimeData<MediaResponse[]>(
    subscribeManyMedia,
    isAdmin,
  );

  // Subscribe to artist bio
  const { data: bioEntries } = useRealtimeData<ArtistBioResponse[]>(
    subscribeManyArtistBio,
    isAdmin,
  );
  const currentBio = bioEntries?.[0] ?? null;

  // Subscribe to WIP posts
  const { data: wipEntries } = useRealtimeData<WipPostsResponse[]>(
    subscribeManyWipPosts,
    isAdmin,
  );
  const allWipPosts = wipEntries ?? [];

  const allMedia = mediaEntries ?? [];

  // Subscribe to team members
  const { data: teamMembersData } = useRealtimeData<TeamMembersResponse[]>(
    subscribeManyTeamMembers,
    isAdmin,
  );
  const allTeamMembers = teamMembersData ?? [];

  // Split media by type
  const galleryImages = useMemo(
    () => [...allMedia.filter(m => m.type === 'image')].sort((a, b) => (a.order ?? 0) - (b.order ?? 0)),
    [allMedia],
  );
  const videoEntry = allMedia.find(m => m.type === 'video') ?? null;
  const heroVideoEntry = allMedia.find(m => m.type === 'hero-video') ?? null;
  const audioEntry = allMedia.find(m => m.type === 'audio') ?? null;

  // Load legal pages on mount (once admin is confirmed)
  useEffect(() => {
    if (!isAdmin || legalLoaded) return;
    setLegalLoaded(true);
    getLegalPages('terms').then(data => {
      setTermsPage(data);
      setTermsContent(data?.content ?? '');
    });
    getLegalPages('privacy').then(data => {
      setPrivacyPage(data);
      setPrivacyContent(data?.content ?? '');
    });
  }, [isAdmin, legalLoaded]);

  // Add image
  const handleAddImage = async (data: { url: string; title: string; order: string }) => {
    setMediaSaving(true);
    try {
      const id = crypto.randomUUID();
      const ok = await setMedia(id, {
        type: 'image',
        url: data.url,
        title: data.title || undefined,
        order: parseInt(data.order, 10) || galleryImages.length + 1,
      });
      if (ok) {
        toast.success('Image added');
        setShowAddImage(false);
      } else {
        toast.error('Failed to add image');
      }
    } catch {
      toast.error('Failed to add image');
    } finally {
      setMediaSaving(false);
    }
  };

  // Edit image
  const handleEditImage = async (id: string, data: { url: string; title: string; order: string }) => {
    setMediaSaving(true);
    try {
      const ok = await updateMedia(id, {
        url: data.url,
        title: data.title || undefined,
        order: parseInt(data.order, 10) || 1,
      });
      if (ok) {
        toast.success('Image updated');
        setEditingMediaId(null);
      } else {
        toast.error('Failed to update image');
      }
    } catch {
      toast.error('Failed to update image');
    } finally {
      setMediaSaving(false);
    }
  };

  // Delete media entry
  const handleDeleteMedia = async (id: string) => {
    setDeletingMediaIds(prev => new Set(prev).add(id));
    try {
      const ok = await deleteMedia(id);
      if (ok) {
        toast.success('Deleted');
      } else {
        toast.error('Failed to delete');
      }
    } catch {
      toast.error('Failed to delete');
    } finally {
      setDeletingMediaIds(prev => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }
  };

  // Add video
  const handleAddVideo = async (data: { url: string; title: string; order: string }) => {
    setMediaSaving(true);
    try {
      const id = crypto.randomUUID();
      const ok = await setMedia(id, {
        type: 'video',
        url: data.url,
        title: data.title || undefined,
        order: 0,
      });
      if (ok) {
        toast.success('Video added');
        setShowAddVideo(false);
      } else {
        toast.error('Failed to add video');
      }
    } catch {
      toast.error('Failed to add video');
    } finally {
      setMediaSaving(false);
    }
  };

  // Edit video
  const handleEditVideo = async (id: string, data: { url: string; title: string; order: string }) => {
    setMediaSaving(true);
    try {
      const ok = await updateMedia(id, {
        url: data.url,
        title: data.title || undefined,
      });
      if (ok) {
        toast.success('Video updated');
        setEditingMediaId(null);
      } else {
        toast.error('Failed to update video');
      }
    } catch {
      toast.error('Failed to update video');
    } finally {
      setMediaSaving(false);
    }
  };

  // Add hero background video
  const handleAddHeroVideo = async (data: { url: string; title: string; order: string }) => {
    setHeroVideoSaving(true);
    try {
      const id = crypto.randomUUID();
      const ok = await setMedia(id, {
        type: 'hero-video',
        url: data.url,
        title: data.title || undefined,
        order: 0,
      });
      if (ok) {
        toast.success('Hero background video saved');
        setShowAddHeroVideo(false);
      } else {
        toast.error('Failed to save hero video');
      }
    } catch {
      toast.error('Failed to save hero video');
    } finally {
      setHeroVideoSaving(false);
    }
  };

  // Edit hero background video
  const handleEditHeroVideo = async (id: string, data: { url: string; title: string; order: string }) => {
    setHeroVideoSaving(true);
    try {
      const ok = await updateMedia(id, {
        url: data.url,
        title: data.title || undefined,
      });
      if (ok) {
        toast.success('Hero video updated');
        setEditingHeroVideoId(null);
      } else {
        toast.error('Failed to update hero video');
      }
    } catch {
      toast.error('Failed to update hero video');
    } finally {
      setHeroVideoSaving(false);
    }
  };

  // Bio image upload
  const handleBioImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBioImgUploading(true);
    setBioImgUploadedUrl(null);
    try {
      const fileId = `${crypto.randomUUID()}-${file.name.replace(/[^a-zA-Z0-9._-]/g, '_')}`;
      const path = `adminFiles/${fileId}`;
      const uploadOk = await setFile(path, file);
      if (!uploadOk) throw new Error('Upload failed');
      const filesResult = await getFiles(path);
      const fileItem = (Array.isArray(filesResult) ? filesResult[0] : null) as { url?: string } | null;
      if (fileItem?.url) {
        setBioImgUploadedUrl(fileItem.url);
        setBioForm(f => ({ ...f, imageUrl: fileItem.url! }));
        toast.success('Image uploaded');
      } else {
        throw new Error('No URL returned');
      }
    } catch {
      toast.error('Image upload failed');
    } finally {
      setBioImgUploading(false);
    }
  };

  // Bio video upload
  const handleBioVideoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const maxSizeMb = 500;
    if (file.size > maxSizeMb * 1024 * 1024) {
      toast.error(`File too large. Maximum size is ${maxSizeMb}MB.`);
      if (bioVideoRef.current) bioVideoRef.current.value = '';
      return;
    }

    setBioVideoUploading(true);
    setBioVideoUploadedUrl(null);
    setBioVideoUploadProgress(`Uploading ${file.name}…`);

    try {
      const fileId = `${crypto.randomUUID()}-${file.name.replace(/[^a-zA-Z0-9._-]/g, '_')}`;
      const path = `adminFiles/${fileId}`;
      const uploadOk = await setFile(path, file);
      if (!uploadOk) throw new Error('Upload returned false');
      const filesResult = await getFiles(path);
      const fileItem = (Array.isArray(filesResult) ? filesResult[0] : null) as { url?: string } | null;
      if (fileItem?.url) {
        setBioVideoUploadedUrl(fileItem.url);
        setBioForm(f => ({ ...f, videoUrl: fileItem.url! }));
        setBioVideoUploadProgress(null);
        toast.success('Video uploaded successfully');
      } else {
        throw new Error('No URL returned after upload');
      }
    } catch (err) {
      console.error('Upload failed', err);
      toast.error('Upload failed. Try again.');
      setBioVideoUploadProgress(null);
      if (bioVideoRef.current) bioVideoRef.current.value = '';
    } finally {
      setBioVideoUploading(false);
    }
  };

  // Save bio
  const handleSaveBio = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!bioForm.name.trim() || !bioForm.role.trim() || !bioForm.bio.trim()) {
      toast.error('Name, role, and bio are required');
      return;
    }
    setBioSaving(true);
    try {
      const bioId = currentBio?.id ?? 'main';
      const ok = currentBio
        ? await updateArtistBio(bioId, {
            name: bioForm.name.trim(),
            role: bioForm.role.trim(),
            bio: bioForm.bio.trim(),
            imageUrl: bioForm.imageUrl || undefined,
            videoUrl: bioForm.videoUrl || undefined,
            xAccount: bioForm.xAccount.trim() || undefined,
          })
        : await setArtistBio(bioId, {
            name: bioForm.name.trim(),
            role: bioForm.role.trim(),
            bio: bioForm.bio.trim(),
            imageUrl: bioForm.imageUrl || undefined,
            videoUrl: bioForm.videoUrl || undefined,
            xAccount: bioForm.xAccount.trim() || undefined,
          });
      if (ok) {
        toast.success(currentBio ? 'Bio updated' : 'Bio created');
        setBioVideoMode('url');
        setBioVideoUploadedUrl(null);
        setBioVideoUploadProgress(null);
        setShowBioForm(false);
      } else {
        toast.error('Failed to save bio');
      }
    } catch {
      toast.error('Failed to save bio');
    } finally {
      setBioSaving(false);
    }
  };

  // WIP media upload
  const handleWipMediaUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setWipMediaUploading(true);
    setWipMediaUploadedUrl(null);
    try {
      const fileId = `${crypto.randomUUID()}-${file.name.replace(/[^a-zA-Z0-9._-]/g, '_')}`;
      const path = `adminFiles/${fileId}`;
      const uploadOk = await setFile(path, file);
      if (!uploadOk) throw new Error('Upload failed');
      const filesResult = await getFiles(path);
      const fileItem = (Array.isArray(filesResult) ? filesResult[0] : null) as { url?: string } | null;
      if (fileItem?.url) {
        setWipMediaUploadedUrl(fileItem.url);
        setWipForm(f => ({ ...f, mediaUrl: fileItem.url! }));
        toast.success('Media uploaded');
      } else {
        throw new Error('No URL returned');
      }
    } catch {
      toast.error('Media upload failed');
    } finally {
      setWipMediaUploading(false);
    }
  };

  // Add WIP post
  const handleAddWipPost = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!wipForm.title.trim()) { toast.error('Title is required'); return; }
    if (!wipForm.mediaUrl) { toast.error('Please upload or enter a media URL'); return; }
    setWipSaving(true);
    try {
      const id = crypto.randomUUID();
      const ok = await setWipPosts(id, {
        title: wipForm.title.trim(),
        description: wipForm.description.trim() || undefined,
        mediaUrl: wipForm.mediaUrl,
        mediaType: wipForm.mediaType,
        order: parseInt(wipForm.order, 10) || allWipPosts.length + 1,
      });
      if (ok) {
        toast.success('WIP post added');
        setShowAddWip(false);
        setWipForm({ title: '', description: '', mediaUrl: '', mediaType: 'image', order: '1' });
        setWipMediaUploadedUrl(null);
      } else {
        toast.error('Failed to add post');
      }
    } catch {
      toast.error('Failed to add post');
    } finally {
      setWipSaving(false);
    }
  };

  // Delete WIP post
  const handleDeleteWip = async (id: string) => {
    setDeletingWipIds(prev => new Set(prev).add(id));
    try {
      const ok = await deleteWipPosts(id);
      if (ok) { toast.success('Post deleted'); }
      else { toast.error('Failed to delete'); }
    } catch {
      toast.error('Failed to delete');
    } finally {
      setDeletingWipIds(prev => { const n = new Set(prev); n.delete(id); return n; });
    }
  };

  // Audio file upload
  const handleAudioFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const maxSizeMb = 100;
    if (file.size > maxSizeMb * 1024 * 1024) {
      toast.error(`File too large. Maximum size is ${maxSizeMb}MB.`);
      if (audioFileRef.current) audioFileRef.current.value = '';
      return;
    }
    setAudioUploading(true);
    setAudioUploadedUrl(null);
    setAudioUploadProgress(`Uploading ${file.name}…`);
    try {
      const fileId = `${crypto.randomUUID()}-${file.name.replace(/[^a-zA-Z0-9._-]/g, '_')}`;
      const path = `adminFiles/${fileId}`;
      const uploadOk = await setFile(path, file);
      if (!uploadOk) throw new Error('Upload returned false');
      const filesResult = await getFiles(path);
      const fileItem = (Array.isArray(filesResult) ? filesResult[0] : null) as { url?: string } | null;
      if (fileItem?.url) {
        setAudioUploadedUrl(fileItem.url);
        setAudioUploadProgress(null);
        toast.success('Audio uploaded successfully');
      } else {
        throw new Error('No URL returned after upload');
      }
    } catch (err) {
      console.error('Audio upload failed', err);
      toast.error('Upload failed. Try again.');
      setAudioUploadProgress(null);
      if (audioFileRef.current) audioFileRef.current.value = '';
    } finally {
      setAudioUploading(false);
    }
  };

  // Save audio entry
  const handleSaveAudio = async () => {
    if (!audioUploadedUrl) {
      toast.error('Please upload an audio file first');
      return;
    }
    setAudioSaving(true);
    try {
      // Delete existing audio entry if present
      if (audioEntry) {
        await deleteMedia(audioEntry.id);
      }
      const id = crypto.randomUUID();
      const ok = await setMedia(id, {
        type: 'audio',
        url: audioUploadedUrl,
        title: audioTitle.trim() || 'Background Music',
        order: 0,
      });
      if (ok) {
        toast.success('Music track saved!');
        setShowAddAudio(false);
        setAudioUploadedUrl(null);
        setAudioTitle('');
        if (audioFileRef.current) audioFileRef.current.value = '';
      } else {
        toast.error('Failed to save audio');
      }
    } catch {
      toast.error('Failed to save audio');
    } finally {
      setAudioSaving(false);
    }
  };

  // Save legal page
  const handleSaveLegalPage = async (pageId: 'terms' | 'privacy') => {
    const content = pageId === 'terms' ? termsContent : privacyContent;
    const setIsSaving = pageId === 'terms' ? setTermsSaving : setPrivacySaving;
    setIsSaving(true);
    try {
      const ok = await setLegalPages(pageId, {
        pageType: pageId,
        content: content.trim(),
        updatedAt: Time.Now,
      });
      if (ok) {
        toast.success(`${pageId === 'terms' ? 'Terms of Service' : 'Privacy Policy'} saved!`);
        // Refresh local state
        const fresh = await getLegalPages(pageId);
        if (pageId === 'terms') { setTermsPage(fresh); }
        else { setPrivacyPage(fresh); }
      } else {
        toast.error('Failed to save — permission denied');
      }
    } catch {
      toast.error('Failed to save');
    } finally {
      setIsSaving(false);
    }
  };

  // Team member handlers
  const handleAddTeamMember = async () => {
    const wallet = teamMemberWallet.trim();
    if (!wallet || wallet.length < 20) {
      toast.error('Enter a valid wallet address');
      return;
    }
    if (!user?.address) return;
    setAddingTeamMember(true);
    try {
      const ok = await setTeamMembers(wallet, {
        walletAddress: wallet,
        addedBy: user.address,
        addedAt: Time.Now,
      });
      if (ok) {
        toast.success('Team member added');
        setTeamMemberWallet('');
      } else {
        toast.error('Failed to add — check permissions');
      }
    } catch {
      toast.error('Failed to add team member');
    } finally {
      setAddingTeamMember(false);
    }
  };

  const handleRemoveTeamMember = async (memberId: string) => {
    if (!window.confirm('Remove this team member?')) return;
    setRemovingTeamMember(memberId);
    try {
      const ok = await deleteTeamMembers(memberId);
      if (ok) {
        toast.success('Team member removed');
      } else {
        toast.error('Failed to remove — check permissions');
      }
    } catch {
      toast.error('Failed to remove team member');
    } finally {
      setRemovingTeamMember(null);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{ background: 'hsl(var(--background))' }}
      >
        <div className="flex flex-col items-center gap-3">
          <div
            className="w-8 h-8 rounded-full border-2 animate-spin"
            style={{ borderColor: '#7CFC00', borderTopColor: 'transparent' }}
          />
          <p className="text-sm" style={{ color: 'rgba(255,255,255,0.4)' }}>
            Loading...
          </p>
        </div>
      </div>
    );
  }

  // Not connected or not admin
  if (!isAdmin) {
    return <AccessDenied login={login} connected={!!user?.address} />;
  }

  return (
    <PageLayout fullBleed footer={false}>
      {/* TV grain */}
      <div className="tv-grain" aria-hidden="true" />

      {/* Top bar */}
      <header
        className="sticky top-0 z-40 flex items-center justify-between px-6 py-4"
        style={{
          background: 'rgba(8,13,31,0.9)',
          backdropFilter: 'blur(12px)',
          borderBottom: '1px solid rgba(124,252,0,0.12)',
        }}
      >
        <div className="flex items-center gap-3">
          <Terminal size={18} style={{ color: '#7CFC00' }} />
          <span
            className="text-xl font-bold uppercase"
            style={{ fontFamily: "'Bangers', cursive", letterSpacing: '0.1em', color: '#7CFC00' }}
          >
            Danger Dan! — Admin
          </span>
        </div>
        <div className="flex items-center gap-2">
          <ShieldCheck size={14} style={{ color: 'rgba(124,252,0,0.6)' }} />
          <span className="text-xs font-mono" style={{ color: 'rgba(255,255,255,0.35)' }}>
            {user.address.slice(0, 6)}…{user.address.slice(-4)}
          </span>
        </div>
      </header>

      <div
        className="container py-8"
        style={{
          background: 'linear-gradient(180deg, rgba(0,0,0,0.7) 0%, rgba(8,13,31,0.9) 30%, rgba(0,0,0,0.95) 100%)',
        }}
      >
        {/* ─── Media Management ──────────────────────────────────────────────── */}
        <div className="mt-14">
          {/* Section divider */}
          <div className="flex items-center gap-4 mb-8">
            <div className="h-px flex-1" style={{ background: 'rgba(124,252,0,0.12)' }} />
            <span
              className="text-xs uppercase tracking-[0.3em] font-bold"
              style={{ color: 'rgba(124,252,0,0.5)' }}
            >
              Media Management
            </span>
            <div className="h-px flex-1" style={{ background: 'rgba(124,252,0,0.12)' }} />
          </div>

          {/* ── Gallery Images ── */}
          <div className="mb-10">
            <div className="flex items-center justify-between mb-5">
              <div className="flex items-center gap-2">
                <Image size={16} style={{ color: '#7CFC00' }} />
                <h3
                  className="text-lg font-bold uppercase"
                  style={{ fontFamily: "'Bangers', cursive", letterSpacing: '0.1em', color: '#7CFC00' }}
                >
                  Gallery Images
                </h3>
                <span
                  className="text-xs font-mono px-2 py-0.5 rounded"
                  style={{ background: 'rgba(124,252,0,0.1)', color: 'rgba(124,252,0,0.7)', border: '1px solid rgba(124,252,0,0.2)' }}
                >
                  {galleryImages.length}
                </span>
              </div>
              {!showAddImage && (
                <button
                  onClick={() => setShowAddImage(true)}
                  className="flex items-center gap-1.5 px-4 py-2 rounded text-xs font-bold uppercase transition-all"
                  style={{
                    fontFamily: "'Bangers', cursive",
                    letterSpacing: '0.1em',
                    background: 'rgba(124,252,0,0.1)',
                    border: '1px solid rgba(124,252,0,0.3)',
                    color: '#7CFC00',
                    cursor: 'pointer',
                  }}
                  onMouseEnter={e => {
                    (e.currentTarget as HTMLButtonElement).style.background = 'rgba(124,252,0,0.2)';
                  }}
                  onMouseLeave={e => {
                    (e.currentTarget as HTMLButtonElement).style.background = 'rgba(124,252,0,0.1)';
                  }}
                >
                  <Plus size={12} />
                  Add Image
                </button>
              )}
            </div>

            {/* Add image form */}
            {showAddImage && (
              <div
                className="rounded-xl p-5 mb-5"
                style={{
                  background: 'rgba(124,252,0,0.04)',
                  border: '1px solid rgba(124,252,0,0.2)',
                }}
              >
                <p
                  className="text-sm font-bold uppercase mb-4"
                  style={{ fontFamily: "'Bangers', cursive", letterSpacing: '0.1em', color: '#7CFC00' }}
                >
                  New Image
                </p>
                <MediaForm
                  type="image"
                  initial={{ url: '', title: '', order: String(galleryImages.length + 1) }}
                  onSubmit={handleAddImage}
                  onCancel={() => setShowAddImage(false)}
                  submitting={mediaSaving}
                />
              </div>
            )}

            {/* Image grid */}
            {galleryImages.length === 0 && !showAddImage ? (
              <div
                className="flex flex-col items-center justify-center py-12 rounded-xl"
                style={{ border: '1px dashed rgba(255,255,255,0.1)', background: 'rgba(255,255,255,0.01)' }}
              >
                <Image size={24} style={{ color: 'rgba(255,255,255,0.15)', marginBottom: 8 }} />
                <p className="text-sm" style={{ color: 'rgba(255,255,255,0.3)' }}>
                  No gallery images yet. Add the first one above.
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                {galleryImages.map(img => {
                  const isEditingThis = editingMediaId === img.id;
                  const isDeleting = deletingMediaIds.has(img.id);
                  return (
                    <div
                      key={img.id}
                      className="rounded-xl overflow-hidden"
                      style={{
                        border: isEditingThis ? '1px solid rgba(124,252,0,0.5)' : '1px solid rgba(255,255,255,0.08)',
                        background: '#0d0d0d',
                        opacity: isDeleting ? 0.4 : 1,
                        transition: 'opacity 0.2s',
                      }}
                    >
                      {isEditingThis ? (
                        <div className="p-4">
                          <MediaForm
                            type="image"
                            initial={{ url: img.url, title: img.title ?? '', order: String(img.order) }}
                            onSubmit={data => handleEditImage(img.id, data)}
                            onCancel={() => setEditingMediaId(null)}
                            submitting={mediaSaving}
                          />
                        </div>
                      ) : (
                        <>
                          {/* Thumbnail */}
                          <div className="relative aspect-[3/4] overflow-hidden">
                            <img
                              src={img.url}
                              alt={img.title ?? 'Gallery image'}
                              loading="lazy"
                              style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                            />
                            {/* Order badge */}
                            <div
                              className="absolute top-2 left-2 w-6 h-6 rounded flex items-center justify-center text-xs font-bold font-mono"
                              style={{ background: 'rgba(0,0,0,0.7)', color: '#7CFC00', border: '1px solid rgba(124,252,0,0.3)' }}
                            >
                              {img.order}
                            </div>
                          </div>
                          {/* Info + actions */}
                          <div className="p-3">
                            <p
                              className="text-xs truncate mb-2"
                              style={{ color: 'rgba(255,255,255,0.7)' }}
                              title={img.title}
                            >
                              {img.title || <span style={{ color: 'rgba(255,255,255,0.25)' }}>No title</span>}
                            </p>
                            <div className="flex gap-2">
                              <button
                                onClick={() => setEditingMediaId(img.id)}
                                className="flex items-center gap-1 px-2.5 py-1 rounded text-xs transition-all"
                                style={{
                                  background: 'rgba(255,255,255,0.05)',
                                  border: '1px solid rgba(255,255,255,0.1)',
                                  color: 'rgba(255,255,255,0.6)',
                                  cursor: 'pointer',
                                }}
                              >
                                <Pencil size={10} />
                                Edit
                              </button>
                              <button
                                onClick={() => handleDeleteMedia(img.id)}
                                disabled={isDeleting}
                                className="flex items-center gap-1 px-2.5 py-1 rounded text-xs transition-all"
                                style={{
                                  background: 'rgba(230,57,70,0.08)',
                                  border: '1px solid rgba(230,57,70,0.2)',
                                  color: 'rgba(230,57,70,0.7)',
                                  cursor: 'pointer',
                                }}
                              >
                                <Trash2 size={10} />
                                Delete
                              </button>
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* ── Hero Background Video ── */}
          <div className="mb-10">
            <div className="flex items-center justify-between mb-5">
              <div className="flex items-center gap-2">
                <Video size={16} style={{ color: '#7CFC00' }} />
                <h3
                  className="text-lg font-bold uppercase"
                  style={{ fontFamily: "'Bangers', cursive", letterSpacing: '0.1em', color: '#7CFC00' }}
                >
                  Hero Background Video
                </h3>
                <span
                  className="text-xs font-mono px-2 py-0.5 rounded"
                  style={{ background: 'rgba(124,252,0,0.1)', color: 'rgba(124,252,0,0.7)', border: '1px solid rgba(124,252,0,0.2)' }}
                >
                  {heroVideoEntry ? 'custom' : 'default'}
                </span>
              </div>
              {!heroVideoEntry && !showAddHeroVideo && (
                <button
                  onClick={() => setShowAddHeroVideo(true)}
                  className="flex items-center gap-1.5 px-4 py-2 rounded text-xs font-bold uppercase transition-all"
                  style={{
                    fontFamily: "'Bangers', cursive",
                    letterSpacing: '0.1em',
                    background: 'rgba(124,252,0,0.1)',
                    border: '1px solid rgba(124,252,0,0.3)',
                    color: '#7CFC00',
                    cursor: 'pointer',
                  }}
                  onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.background = 'rgba(124,252,0,0.2)'; }}
                  onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.background = 'rgba(124,252,0,0.1)'; }}
                >
                  <Plus size={12} />
                  Set Custom Video
                </button>
              )}
            </div>

            {/* Explanatory note */}
            <p className="text-xs mb-4 font-mono" style={{ color: 'rgba(255,255,255,0.3)' }}>
              Desktop-only background video shown behind the hero logo. Defaults to the built-in YouTube video if no custom video is set. Paste a YouTube URL or upload an MP4.
            </p>

            {/* Add hero video form */}
            {showAddHeroVideo && (
              <div
                className="rounded-xl p-5 mb-5"
                style={{
                  background: 'rgba(124,252,0,0.04)',
                  border: '1px solid rgba(124,252,0,0.2)',
                  maxWidth: 520,
                }}
              >
                <p
                  className="text-sm font-bold uppercase mb-4"
                  style={{ fontFamily: "'Bangers', cursive", letterSpacing: '0.1em', color: '#7CFC00' }}
                >
                  Set Hero Background Video
                </p>
                <VideoForm
                  initial={{ url: '', title: '', order: '0' }}
                  onSubmit={handleAddHeroVideo}
                  onCancel={() => setShowAddHeroVideo(false)}
                  submitting={heroVideoSaving}
                />
              </div>
            )}

            {/* Default state — no custom hero video */}
            {!heroVideoEntry && !showAddHeroVideo && (
              <div
                className="flex flex-col gap-2 px-5 py-4 rounded-xl"
                style={{
                  border: '1px solid rgba(124,252,0,0.1)',
                  background: 'rgba(124,252,0,0.03)',
                  maxWidth: 520,
                }}
              >
                <div className="flex items-center gap-2">
                  <div
                    className="w-2 h-2 rounded-full"
                    style={{ background: '#7CFC00', boxShadow: '0 0 6px #7CFC00' }}
                  />
                  <span className="text-xs font-bold uppercase" style={{ fontFamily: "'Bangers', cursive", letterSpacing: '0.1em', color: 'rgba(124,252,0,0.7)' }}>
                    Using Default YouTube Video
                  </span>
                </div>
                <p className="text-xs font-mono" style={{ color: 'rgba(255,255,255,0.25)' }}>
                  ID: R5P5u32vFaQ — set a custom video above to override
                </p>
              </div>
            )}

            {/* Current hero video entry — view mode */}
            {heroVideoEntry && editingHeroVideoId !== heroVideoEntry.id && (
              <div
                className="rounded-xl overflow-hidden"
                style={{ border: '1px solid rgba(124,252,0,0.25)', background: '#0d0d0d', maxWidth: 520 }}
              >
                <div className="relative" style={{ paddingTop: '56.25%' }}>
                  {heroVideoEntry.url.includes('youtube') || heroVideoEntry.url.includes('youtu.be') ? (
                    <iframe
                      src={heroVideoEntry.url}
                      title={heroVideoEntry.title ?? 'Hero background video'}
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 0 }}
                    />
                  ) : (
                    <video
                      src={heroVideoEntry.url}
                      controls
                      style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}
                    />
                  )}
                </div>
                <div className="px-4 py-3 flex items-center justify-between">
                  <div>
                    <p className="text-xs font-bold uppercase mb-0.5" style={{ fontFamily: "'Bangers', cursive", letterSpacing: '0.08em', color: 'rgba(124,252,0,0.7)' }}>
                      Hero Background Video
                    </p>
                    <p className="text-xs font-mono truncate" style={{ color: 'rgba(255,255,255,0.35)', maxWidth: 260 }} title={heroVideoEntry.url}>
                      {heroVideoEntry.url}
                    </p>
                  </div>
                  <div className="flex gap-2 shrink-0 ml-3">
                    <button
                      onClick={() => setEditingHeroVideoId(heroVideoEntry.id)}
                      className="flex items-center gap-1 px-2.5 py-1 rounded text-xs"
                      style={{
                        background: 'rgba(255,255,255,0.05)',
                        border: '1px solid rgba(255,255,255,0.1)',
                        color: 'rgba(255,255,255,0.6)',
                        cursor: 'pointer',
                      }}
                    >
                      <Pencil size={10} />
                      Edit
                    </button>
                    <button
                      onClick={() => handleDeleteMedia(heroVideoEntry.id)}
                      disabled={deletingMediaIds.has(heroVideoEntry.id)}
                      className="flex items-center gap-1 px-2.5 py-1 rounded text-xs"
                      style={{
                        background: 'rgba(230,57,70,0.08)',
                        border: '1px solid rgba(230,57,70,0.2)',
                        color: 'rgba(230,57,70,0.7)',
                        cursor: 'pointer',
                      }}
                    >
                      <Trash2 size={10} />
                      Revert to Default
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Hero video edit form */}
            {heroVideoEntry && editingHeroVideoId === heroVideoEntry.id && (
              <div
                className="rounded-xl p-5"
                style={{
                  background: 'rgba(124,252,0,0.04)',
                  border: '1px solid rgba(124,252,0,0.25)',
                  maxWidth: 520,
                }}
              >
                <p
                  className="text-sm font-bold uppercase mb-4"
                  style={{ fontFamily: "'Bangers', cursive", letterSpacing: '0.1em', color: '#7CFC00' }}
                >
                  Edit Hero Background Video
                </p>
                <VideoForm
                  initial={{ url: heroVideoEntry.url, title: heroVideoEntry.title ?? '', order: '0' }}
                  onSubmit={data => handleEditHeroVideo(heroVideoEntry.id, data)}
                  onCancel={() => setEditingHeroVideoId(null)}
                  submitting={heroVideoSaving}
                />
              </div>
            )}
          </div>

          {/* ── Video Teaser ── */}
          <div>
            <div className="flex items-center justify-between mb-5">
              <div className="flex items-center gap-2">
                <Film size={16} style={{ color: '#E63946' }} />
                <h3
                  className="text-lg font-bold uppercase"
                  style={{ fontFamily: "'Bangers', cursive", letterSpacing: '0.1em', color: '#E63946' }}
                >
                  Video Teaser
                </h3>
                <span
                  className="text-xs font-mono px-2 py-0.5 rounded"
                  style={{ background: 'rgba(230,57,70,0.1)', color: 'rgba(230,57,70,0.7)', border: '1px solid rgba(230,57,70,0.2)' }}
                >
                  {videoEntry ? '1 / 1' : '0 / 1'}
                </span>
              </div>
              {!videoEntry && !showAddVideo && (
                <button
                  onClick={() => setShowAddVideo(true)}
                  className="flex items-center gap-1.5 px-4 py-2 rounded text-xs font-bold uppercase transition-all"
                  style={{
                    fontFamily: "'Bangers', cursive",
                    letterSpacing: '0.1em',
                    background: 'rgba(230,57,70,0.1)',
                    border: '1px solid rgba(230,57,70,0.3)',
                    color: '#E63946',
                    cursor: 'pointer',
                  }}
                  onMouseEnter={e => {
                    (e.currentTarget as HTMLButtonElement).style.background = 'rgba(230,57,70,0.2)';
                  }}
                  onMouseLeave={e => {
                    (e.currentTarget as HTMLButtonElement).style.background = 'rgba(230,57,70,0.1)';
                  }}
                >
                  <Plus size={12} />
                  Add Video
                </button>
              )}
            </div>

            {/* Add video form */}
            {showAddVideo && (
              <div
                className="rounded-xl p-5 mb-5"
                style={{
                  background: 'rgba(230,57,70,0.04)',
                  border: '1px solid rgba(230,57,70,0.2)',
                }}
              >
                <p
                  className="text-sm font-bold uppercase mb-4"
                  style={{ fontFamily: "'Bangers', cursive", letterSpacing: '0.1em', color: '#E63946' }}
                >
                  New Video Teaser
                </p>
                <VideoForm
                  initial={{ url: '', title: '', order: '0' }}
                  onSubmit={handleAddVideo}
                  onCancel={() => setShowAddVideo(false)}
                  submitting={mediaSaving}
                />
              </div>
            )}

            {/* Video display */}
            {!videoEntry && !showAddVideo && (
              <div
                className="flex flex-col items-center justify-center py-12 rounded-xl"
                style={{ border: '1px dashed rgba(255,255,255,0.1)', background: 'rgba(255,255,255,0.01)' }}
              >
                <Video size={24} style={{ color: 'rgba(255,255,255,0.15)', marginBottom: 8 }} />
                <p className="text-sm" style={{ color: 'rgba(255,255,255,0.3)' }}>
                  No video teaser yet. Add one above.
                </p>
              </div>
            )}

            {videoEntry && !editingMediaId && (
              <div
                className="rounded-xl overflow-hidden"
                style={{ border: '1px solid rgba(230,57,70,0.2)', background: '#0d0d0d', maxWidth: 520 }}
              >
                <div className="relative" style={{ paddingTop: '56.25%' }}>
                  {videoEntry.url.includes('youtube') || videoEntry.url.includes('youtu.be') ? (
                    <iframe
                      src={videoEntry.url}
                      title={videoEntry.title ?? 'Video teaser'}
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 0 }}
                    />
                  ) : (
                    <video
                      src={videoEntry.url}
                      controls
                      style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}
                    />
                  )}
                </div>
                <div className="px-4 py-3 flex items-center justify-between">
                  <p className="text-sm font-medium" style={{ color: 'rgba(255,255,255,0.7)' }}>
                    {videoEntry.title || <span style={{ color: 'rgba(255,255,255,0.25)' }}>No title</span>}
                  </p>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setEditingMediaId(videoEntry.id)}
                      className="flex items-center gap-1 px-2.5 py-1 rounded text-xs"
                      style={{
                        background: 'rgba(255,255,255,0.05)',
                        border: '1px solid rgba(255,255,255,0.1)',
                        color: 'rgba(255,255,255,0.6)',
                        cursor: 'pointer',
                      }}
                    >
                      <Pencil size={10} />
                      Edit
                    </button>
                    <button
                      onClick={() => handleDeleteMedia(videoEntry.id)}
                      disabled={deletingMediaIds.has(videoEntry.id)}
                      className="flex items-center gap-1 px-2.5 py-1 rounded text-xs"
                      style={{
                        background: 'rgba(230,57,70,0.08)',
                        border: '1px solid rgba(230,57,70,0.2)',
                        color: 'rgba(230,57,70,0.7)',
                        cursor: 'pointer',
                      }}
                    >
                      <Trash2 size={10} />
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Video edit form */}
            {videoEntry && editingMediaId === videoEntry.id && (
              <div
                className="rounded-xl p-5"
                style={{
                  background: 'rgba(230,57,70,0.04)',
                  border: '1px solid rgba(230,57,70,0.25)',
                  maxWidth: 520,
                }}
              >
                <p
                  className="text-sm font-bold uppercase mb-4"
                  style={{ fontFamily: "'Bangers', cursive", letterSpacing: '0.1em', color: '#E63946' }}
                >
                  Edit Video Teaser
                </p>
                <VideoForm
                  initial={{ url: videoEntry.url, title: videoEntry.title ?? '', order: '0' }}
                  onSubmit={data => handleEditVideo(videoEntry.id, data)}
                  onCancel={() => setEditingMediaId(null)}
                  submitting={mediaSaving}
                />
              </div>
            )}
          </div>
        </div>

        {/* ─── Artist Bio Management ───────────────────────────────────────── */}
        <div className="mt-14">
          <div className="flex items-center gap-4 mb-8">
            <div className="h-px flex-1" style={{ background: 'rgba(124,252,0,0.12)' }} />
            <span
              className="text-xs uppercase tracking-[0.3em] font-bold"
              style={{ color: 'rgba(124,252,0,0.5)' }}
            >
              Artist Room
            </span>
            <div className="h-px flex-1" style={{ background: 'rgba(124,252,0,0.12)' }} />
          </div>

          {/* ── Bio ── */}
          <div className="mb-10">
            <div className="flex items-center justify-between mb-5">
              <div className="flex items-center gap-2">
                <Palette size={16} style={{ color: '#7CFC00' }} />
                <h3
                  className="text-lg font-bold uppercase"
                  style={{ fontFamily: "'Bangers', cursive", letterSpacing: '0.1em', color: '#7CFC00' }}
                >
                  Artist Bio
                </h3>
                <span
                  className="text-xs font-mono px-2 py-0.5 rounded"
                  style={{ background: 'rgba(124,252,0,0.1)', color: 'rgba(124,252,0,0.7)', border: '1px solid rgba(124,252,0,0.2)' }}
                >
                  {currentBio ? 'set' : 'empty'}
                </span>
              </div>
              {!showBioForm && (
                <button
                  onClick={() => {
                    if (currentBio) {
                      setBioForm({
                        name: currentBio.name,
                        role: currentBio.role,
                        bio: currentBio.bio,
                        imageUrl: currentBio.imageUrl ?? '',
                        videoUrl: currentBio.videoUrl ?? '',
                        xAccount: currentBio.xAccount ?? '',
                      });
                    }
                    setBioVideoMode('url');
                    setBioVideoUploadedUrl(null);
                    setBioVideoUploadProgress(null);
                    setShowBioForm(true);
                  }}
                  className="flex items-center gap-1.5 px-4 py-2 rounded text-xs font-bold uppercase transition-all"
                  style={{
                    fontFamily: "'Bangers', cursive",
                    letterSpacing: '0.1em',
                    background: 'rgba(124,252,0,0.1)',
                    border: '1px solid rgba(124,252,0,0.3)',
                    color: '#7CFC00',
                    cursor: 'pointer',
                  }}
                  onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.background = 'rgba(124,252,0,0.2)'; }}
                  onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.background = 'rgba(124,252,0,0.1)'; }}
                >
                  {currentBio ? <><Pencil size={12} /> Edit Bio</> : <><Plus size={12} /> Create Bio</>}
                </button>
              )}
            </div>

            {/* Current bio preview */}
            {currentBio && !showBioForm && (
              <div
                className="rounded-xl overflow-hidden flex gap-4 p-4"
                style={{ border: '1px solid rgba(124,252,0,0.2)', background: 'rgba(124,252,0,0.03)', maxWidth: 560 }}
              >
                {currentBio.imageUrl && (
                  <img
                    src={currentBio.imageUrl}
                    alt={currentBio.name}
                    style={{ width: 80, height: 80, objectFit: 'cover', border: '2px solid rgba(124,252,0,0.3)', borderRadius: 4, flexShrink: 0 }}
                  />
                )}
                <div>
                  <p style={{ fontFamily: "'Bangers', cursive", fontSize: '1.1rem', letterSpacing: '0.08em', color: '#7CFC00' }}>
                    {currentBio.name}
                  </p>
                  <p style={{ fontSize: '0.8rem', color: '#E63946', fontFamily: "'Bangers', cursive", letterSpacing: '0.12em' }}>
                    {currentBio.role}
                  </p>
                  <p style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.5)', marginTop: '4px', lineHeight: 1.5 }}>
                    {currentBio.bio.slice(0, 120)}{currentBio.bio.length > 120 ? '…' : ''}
                  </p>
                  {currentBio.xAccount && (
                    <p style={{ fontSize: '0.7rem', color: '#38BDF8', marginTop: '4px', fontFamily: 'monospace' }}>
                      {currentBio.xAccount}
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Bio form */}
            {showBioForm && (
              <div
                className="rounded-xl p-5"
                style={{ background: 'rgba(124,252,0,0.04)', border: '1px solid rgba(124,252,0,0.2)', maxWidth: 560 }}
              >
                <p
                  className="text-sm font-bold uppercase mb-4"
                  style={{ fontFamily: "'Bangers', cursive", letterSpacing: '0.1em', color: '#7CFC00' }}
                >
                  {currentBio ? 'Edit Artist Bio' : 'Create Artist Bio'}
                </p>
                <form onSubmit={handleSaveBio} className="flex flex-col gap-3">
                  <div>
                    <label className="block text-xs uppercase tracking-widest mb-1.5" style={{ fontFamily: "'Bangers', cursive", letterSpacing: '0.15em', color: 'rgba(124,252,0,0.6)' }}>Name *</label>
                    <input
                      style={inputStyle}
                      value={bioForm.name}
                      onChange={e => setBioForm(f => ({ ...f, name: e.target.value }))}
                      placeholder="Artist name..."
                      disabled={bioSaving}
                    />
                  </div>
                  <div>
                    <label className="block text-xs uppercase tracking-widest mb-1.5" style={{ fontFamily: "'Bangers', cursive", letterSpacing: '0.15em', color: 'rgba(124,252,0,0.6)' }}>Role *</label>
                    <input
                      style={inputStyle}
                      value={bioForm.role}
                      onChange={e => setBioForm(f => ({ ...f, role: e.target.value }))}
                      placeholder="e.g. Lead Artist, Illustrator..."
                      disabled={bioSaving}
                    />
                  </div>
                  <div>
                    <label className="block text-xs uppercase tracking-widest mb-1.5" style={{ fontFamily: "'Bangers', cursive", letterSpacing: '0.15em', color: 'rgba(124,252,0,0.6)' }}>Bio *</label>
                    <textarea
                      style={{ ...inputStyle, minHeight: '90px', resize: 'vertical' }}
                      value={bioForm.bio}
                      onChange={e => setBioForm(f => ({ ...f, bio: e.target.value }))}
                      placeholder="Artist biography..."
                      disabled={bioSaving}
                    />
                  </div>
                  {/* X (Twitter) Account — shown for both create and edit */}
                  <div>
                    <label className="block text-xs uppercase tracking-widest mb-1.5" style={{ color: 'rgba(255,255,255,0.4)' }}>
                      X (Twitter) Account
                    </label>
                    <input
                      style={inputStyle}
                      value={bioForm.xAccount}
                      onChange={e => setBioForm(f => ({ ...f, xAccount: e.target.value }))}
                      placeholder="@username or https://x.com/username"
                      disabled={bioSaving}
                    />
                  </div>
                  {/* Image upload */}
                  <div>
                    <label className="block text-xs uppercase tracking-widest mb-1.5" style={{ color: 'rgba(255,255,255,0.4)' }}>
                      Profile Image
                    </label>
                    <div className="flex items-center gap-2">
                      <input
                        style={{ ...inputStyle, flex: 1 }}
                        value={bioForm.imageUrl}
                        onChange={e => setBioForm(f => ({ ...f, imageUrl: e.target.value }))}
                        placeholder="https://... or upload below"
                        disabled={bioSaving || bioImgUploading}
                      />
                      <button
                        type="button"
                        onClick={() => bioImgRef.current?.click()}
                        disabled={bioImgUploading || bioSaving}
                        className="flex items-center gap-1 px-3 py-2 rounded text-xs transition-all"
                        style={{
                          background: 'rgba(230,57,70,0.1)',
                          border: '1px solid rgba(230,57,70,0.3)',
                          color: '#E63946',
                          cursor: 'pointer',
                          fontFamily: "'Bangers', cursive",
                          letterSpacing: '0.08em',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        <Upload size={11} />
                        {bioImgUploading ? 'Uploading…' : 'Upload'}
                      </button>
                    </div>
                    <input
                      ref={bioImgRef}
                      type="file"
                      accept="image/*"
                      onChange={handleBioImageUpload}
                      style={{ display: 'none' }}
                    />
                    {bioImgUploadedUrl && (
                      <p className="text-xs mt-1.5" style={{ color: 'rgba(124,252,0,0.6)' }}>
                        Uploaded: {bioImgUploadedUrl.slice(0, 50)}…
                      </p>
                    )}
                  </div>
                  {/* Video/Reel URL or Upload */}
                  <div>
                    <label className="block text-xs uppercase tracking-widest mb-2" style={{ color: 'rgba(255,255,255,0.4)' }}>
                      Video/Reel (optional)
                    </label>
                    {/* Mode toggle */}
                    <div className="flex items-center gap-2 mb-3">
                      <button
                        type="button"
                        onClick={() => { setBioVideoMode('url'); setBioVideoUploadedUrl(null); }}
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-bold uppercase transition-all"
                        style={{
                          fontFamily: "'Bangers', cursive",
                          letterSpacing: '0.08em',
                          background: bioVideoMode === 'url' ? 'rgba(230,57,70,0.18)' : 'rgba(255,255,255,0.04)',
                          border: bioVideoMode === 'url' ? '1px solid rgba(230,57,70,0.5)' : '1px solid rgba(255,255,255,0.08)',
                          color: bioVideoMode === 'url' ? '#E63946' : 'rgba(255,255,255,0.4)',
                          cursor: 'pointer',
                        }}
                      >
                        <Link size={11} />
                        Paste URL
                      </button>
                      <button
                        type="button"
                        onClick={() => setBioVideoMode('upload')}
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-bold uppercase transition-all"
                        style={{
                          fontFamily: "'Bangers', cursive",
                          letterSpacing: '0.08em',
                          background: bioVideoMode === 'upload' ? 'rgba(230,57,70,0.18)' : 'rgba(255,255,255,0.04)',
                          border: bioVideoMode === 'upload' ? '1px solid rgba(230,57,70,0.5)' : '1px solid rgba(255,255,255,0.08)',
                          color: bioVideoMode === 'upload' ? '#E63946' : 'rgba(255,255,255,0.4)',
                          cursor: 'pointer',
                        }}
                      >
                        <Upload size={11} />
                        Upload File
                      </button>
                    </div>

                    {/* URL input */}
                    {bioVideoMode === 'url' && (
                      <input
                        style={inputStyle}
                        value={bioForm.videoUrl}
                        onChange={e => setBioForm(f => ({ ...f, videoUrl: e.target.value }))}
                        placeholder="https://..."
                        disabled={bioSaving || bioVideoUploading}
                      />
                    )}

                    {/* File upload */}
                    {bioVideoMode === 'upload' && (
                      <div>
                        <div
                          className="relative flex flex-col items-center justify-center gap-2 rounded-lg transition-all p-4"
                          style={{
                            border: bioVideoUploadedUrl
                              ? '1px solid rgba(124,252,0,0.4)'
                              : '2px dashed rgba(230,57,70,0.3)',
                            background: bioVideoUploadedUrl
                              ? 'rgba(124,252,0,0.04)'
                              : 'rgba(230,57,70,0.04)',
                          }}
                        >
                          {bioVideoUploadedUrl ? (
                            <>
                              <p className="text-xs font-bold uppercase" style={{ color: 'rgba(124,252,0,0.7)' }}>
                                ✓ Uploaded
                              </p>
                              <p className="text-xs" style={{ color: 'rgba(124,252,0,0.5)' }}>
                                {bioVideoUploadedUrl.split('/').pop()?.slice(0, 40)}…
                              </p>
                            </>
                          ) : (
                            <>
                              <p className="text-xs font-bold uppercase" style={{ color: 'rgba(255,255,255,0.4)' }}>
                                {bioVideoUploadProgress || 'Select Video File'}
                              </p>
                              <p className="text-xs" style={{ color: 'rgba(255,255,255,0.3)' }}>
                                MP4, WebM, MOV — max 500 MB
                              </p>
                            </>
                          )}
                          <input
                            ref={bioVideoRef}
                            type="file"
                            accept="video/*"
                            onChange={handleBioVideoUpload}
                            disabled={bioVideoUploading || bioSaving}
                            style={{
                              position: 'absolute',
                              width: '100%',
                              height: '100%',
                              opacity: 0,
                              cursor: bioVideoUploading || bioSaving ? 'not-allowed' : 'pointer',
                            }}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="flex gap-2 mt-1">
                    <button
                      type="submit"
                      disabled={bioSaving}
                      className="flex items-center gap-1.5 px-4 py-2 rounded text-xs font-bold uppercase transition-all"
                      style={{
                        fontFamily: "'Bangers', cursive",
                        letterSpacing: '0.1em',
                        background: bioSaving ? 'rgba(124,252,0,0.08)' : 'rgba(124,252,0,0.15)',
                        border: '1px solid rgba(124,252,0,0.4)',
                        color: '#7CFC00',
                        cursor: bioSaving ? 'not-allowed' : 'pointer',
                      }}
                    >
                      <Check size={12} />
                      {bioSaving ? 'Saving…' : 'Save Bio'}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setBioVideoMode('url');
                        setBioVideoUploadedUrl(null);
                        setBioVideoUploadProgress(null);
                        setShowBioForm(false);
                      }}
                      disabled={bioSaving}
                      className="flex items-center gap-1.5 px-4 py-2 rounded text-xs font-bold uppercase transition-all"
                      style={{
                        fontFamily: "'Bangers', cursive",
                        letterSpacing: '0.1em',
                        background: 'rgba(255,255,255,0.04)',
                        border: '1px solid rgba(255,255,255,0.1)',
                        color: 'rgba(255,255,255,0.5)',
                        cursor: 'pointer',
                      }}
                    >
                      <X size={12} /> Cancel
                    </button>
                  </div>
                </form>
              </div>
            )}

            {!currentBio && !showBioForm && (
              <div
                className="flex flex-col items-center justify-center py-10 rounded-xl"
                style={{ border: '1px dashed rgba(255,255,255,0.1)', background: 'rgba(255,255,255,0.01)' }}
              >
                <p className="text-sm" style={{ color: 'rgba(255,255,255,0.3)' }}>
                  No artist bio yet. Create one above.
                </p>
              </div>
            )}
          </div>

          {/* ── WIP Posts ── */}
          <div>
            <div className="flex items-center justify-between mb-5">
              <div className="flex items-center gap-2">
                <Image size={16} style={{ color: '#E63946' }} />
                <h3
                  className="text-lg font-bold uppercase"
                  style={{ fontFamily: "'Bangers', cursive", letterSpacing: '0.1em', color: '#E63946' }}
                >
                  WIP Posts
                </h3>
                <span
                  className="text-xs font-mono px-2 py-0.5 rounded"
                  style={{ background: 'rgba(230,57,70,0.1)', color: 'rgba(230,57,70,0.7)', border: '1px solid rgba(230,57,70,0.2)' }}
                >
                  {allWipPosts.length}
                </span>
              </div>
              {!showAddWip && (
                <button
                  onClick={() => setShowAddWip(true)}
                  className="flex items-center gap-1.5 px-4 py-2 rounded text-xs font-bold uppercase transition-all"
                  style={{
                    fontFamily: "'Bangers', cursive",
                    letterSpacing: '0.1em',
                    background: 'rgba(230,57,70,0.1)',
                    border: '1px solid rgba(230,57,70,0.3)',
                    color: '#E63946',
                    cursor: 'pointer',
                  }}
                  onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.background = 'rgba(230,57,70,0.2)'; }}
                  onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.background = 'rgba(230,57,70,0.1)'; }}
                >
                  <Plus size={12} />
                  Add Post
                </button>
              )}
            </div>

            {/* Add WIP form */}
            {showAddWip && (
              <div
                className="rounded-xl p-5 mb-6"
                style={{ background: 'rgba(230,57,70,0.04)', border: '1px solid rgba(230,57,70,0.2)', maxWidth: 560 }}
              >
                <p
                  className="text-sm font-bold uppercase mb-4"
                  style={{ fontFamily: "'Bangers', cursive", letterSpacing: '0.1em', color: '#E63946' }}
                >
                  New WIP Post
                </p>
                <form onSubmit={handleAddWipPost} className="flex flex-col gap-3">
                  <div>
                    <label className="block text-xs uppercase tracking-widest mb-1.5" style={{ color: 'rgba(255,255,255,0.4)' }}>Title *</label>
                    <input
                      style={inputStyle}
                      value={wipForm.title}
                      onChange={e => setWipForm(f => ({ ...f, title: e.target.value }))}
                      placeholder="Post title..."
                      disabled={wipSaving}
                    />
                  </div>
                  <div>
                    <label className="block text-xs uppercase tracking-widest mb-1.5" style={{ color: 'rgba(255,255,255,0.4)' }}>Description (optional)</label>
                    <input
                      style={inputStyle}
                      value={wipForm.description}
                      onChange={e => setWipForm(f => ({ ...f, description: e.target.value }))}
                      placeholder="Short description..."
                      disabled={wipSaving}
                    />
                  </div>
                  {/* Media type */}
                  <div>
                    <label className="block text-xs uppercase tracking-widest mb-1.5" style={{ color: 'rgba(255,255,255,0.4)' }}>Media Type</label>
                    <div className="flex gap-2">
                      {(['image', 'video'] as const).map(t => (
                        <button
                          key={t}
                          type="button"
                          onClick={() => setWipForm(f => ({ ...f, mediaType: t }))}
                          className="px-3 py-1.5 rounded text-xs font-bold uppercase transition-all"
                          style={{
                            fontFamily: "'Bangers', cursive",
                            letterSpacing: '0.08em',
                            background: wipForm.mediaType === t ? 'rgba(230,57,70,0.18)' : 'rgba(255,255,255,0.04)',
                            border: wipForm.mediaType === t ? '1px solid rgba(230,57,70,0.5)' : '1px solid rgba(255,255,255,0.08)',
                            color: wipForm.mediaType === t ? '#E63946' : 'rgba(255,255,255,0.4)',
                            cursor: 'pointer',
                          }}
                        >
                          {t}
                        </button>
                      ))}
                    </div>
                  </div>
                  {/* Media upload/url */}
                  <div>
                    <label className="block text-xs uppercase tracking-widest mb-1.5" style={{ color: 'rgba(255,255,255,0.4)' }}>
                      Media URL *
                    </label>
                    <div className="flex items-center gap-2">
                      <input
                        style={{ ...inputStyle, flex: 1 }}
                        value={wipForm.mediaUrl}
                        onChange={e => setWipForm(f => ({ ...f, mediaUrl: e.target.value }))}
                        placeholder="https://... or upload"
                        disabled={wipSaving || wipMediaUploading}
                      />
                      <button
                        type="button"
                        onClick={() => wipMediaRef.current?.click()}
                        disabled={wipMediaUploading || wipSaving}
                        className="flex items-center gap-1 px-3 py-2 rounded text-xs transition-all"
                        style={{
                          background: 'rgba(230,57,70,0.1)',
                          border: '1px solid rgba(230,57,70,0.3)',
                          color: '#E63946',
                          cursor: 'pointer',
                          fontFamily: "'Bangers', cursive",
                          letterSpacing: '0.08em',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        <Upload size={11} />
                        {wipMediaUploading ? 'Uploading…' : 'Upload'}
                      </button>
                    </div>
                    <input
                      ref={wipMediaRef}
                      type="file"
                      accept={wipForm.mediaType === 'video' ? 'video/*' : 'image/*'}
                      onChange={handleWipMediaUpload}
                      style={{ display: 'none' }}
                    />
                    {wipMediaUploadedUrl && (
                      <p className="text-xs mt-1.5" style={{ color: 'rgba(124,252,0,0.6)' }}>
                        Uploaded successfully
                      </p>
                    )}
                  </div>
                  {/* Order */}
                  <div>
                    <label className="block text-xs uppercase tracking-widest mb-1.5" style={{ color: 'rgba(255,255,255,0.4)' }}>Display Order</label>
                    <input
                      style={{ ...inputStyle, width: '80px' }}
                      type="number"
                      min="1"
                      value={wipForm.order}
                      onChange={e => setWipForm(f => ({ ...f, order: e.target.value }))}
                      disabled={wipSaving}
                    />
                  </div>
                  <div className="flex gap-2 mt-1">
                    <button
                      type="submit"
                      disabled={wipSaving}
                      className="flex items-center gap-1.5 px-4 py-2 rounded text-xs font-bold uppercase transition-all"
                      style={{
                        fontFamily: "'Bangers', cursive",
                        letterSpacing: '0.1em',
                        background: wipSaving ? 'rgba(230,57,70,0.08)' : 'rgba(230,57,70,0.15)',
                        border: '1px solid rgba(230,57,70,0.4)',
                        color: '#E63946',
                        cursor: wipSaving ? 'not-allowed' : 'pointer',
                      }}
                    >
                      <Check size={12} />
                      {wipSaving ? 'Saving…' : 'Add Post'}
                    </button>
                    <button
                      type="button"
                      onClick={() => { setShowAddWip(false); setWipMediaUploadedUrl(null); }}
                      disabled={wipSaving}
                      className="flex items-center gap-1.5 px-4 py-2 rounded text-xs font-bold uppercase transition-all"
                      style={{
                        fontFamily: "'Bangers', cursive",
                        letterSpacing: '0.1em',
                        background: 'rgba(255,255,255,0.04)',
                        border: '1px solid rgba(255,255,255,0.1)',
                        color: 'rgba(255,255,255,0.5)',
                        cursor: 'pointer',
                      }}
                    >
                      <X size={12} /> Cancel
                    </button>
                  </div>
                </form>
              </div>
            )}

            {/* WIP posts list */}
            {allWipPosts.length === 0 && !showAddWip ? (
              <div
                className="flex flex-col items-center justify-center py-10 rounded-xl"
                style={{ border: '1px dashed rgba(255,255,255,0.1)', background: 'rgba(255,255,255,0.01)' }}
              >
                <p className="text-sm" style={{ color: 'rgba(255,255,255,0.3)' }}>
                  No WIP posts yet. Add the first one above.
                </p>
              </div>
            ) : (
              <div className="flex flex-col gap-3">
                {[...allWipPosts]
                  .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
                  .map(post => {
                    const isDeleting = deletingWipIds.has(post.id);
                    return (
                      <div
                        key={post.id}
                        className="flex items-center gap-4 rounded-xl p-4"
                        style={{
                          background: 'rgba(255,255,255,0.02)',
                          border: '1px solid rgba(255,255,255,0.07)',
                          opacity: isDeleting ? 0.4 : 1,
                          transition: 'opacity 0.2s',
                        }}
                      >
                        {/* Thumbnail */}
                        <div
                          style={{
                            width: 56,
                            height: 56,
                            flexShrink: 0,
                            background: '#0d0d0d',
                            border: '1px solid rgba(255,255,255,0.08)',
                            overflow: 'hidden',
                            borderRadius: 4,
                          }}
                        >
                          {post.mediaType === 'video' ? (
                            <video
                              src={post.mediaUrl}
                              muted
                              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                            />
                          ) : (
                            <img
                              src={post.mediaUrl}
                              alt={post.title}
                              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                            />
                          )}
                        </div>
                        {/* Info */}
                        <div className="flex-1 min-w-0">
                          <p
                            className="truncate font-bold text-sm"
                            style={{ color: 'rgba(255,255,255,0.85)' }}
                          >
                            {post.title}
                          </p>
                          <div className="flex items-center gap-2 mt-0.5">
                            <span
                              className="text-xs px-1.5 py-0.5 rounded"
                              style={{
                                background: post.mediaType === 'video' ? 'rgba(230,57,70,0.12)' : 'rgba(124,252,0,0.08)',
                                color: post.mediaType === 'video' ? '#E63946' : '#7CFC00',
                                border: `1px solid ${post.mediaType === 'video' ? 'rgba(230,57,70,0.25)' : 'rgba(124,252,0,0.2)'}`,
                                fontFamily: 'monospace',
                                fontSize: '0.65rem',
                              }}
                            >
                              {post.mediaType}
                            </span>
                            <span className="text-xs font-mono" style={{ color: 'rgba(255,255,255,0.25)' }}>
                              order: {post.order}
                            </span>
                          </div>
                        </div>
                        {/* Delete */}
                        <button
                          onClick={() => handleDeleteWip(post.id)}
                          disabled={isDeleting}
                          className="w-7 h-7 rounded flex items-center justify-center transition-all duration-150"
                          style={{
                            background: 'transparent',
                            border: '1px solid rgba(230,57,70,0.2)',
                            color: 'rgba(230,57,70,0.45)',
                            cursor: 'pointer',
                          }}
                          onMouseEnter={e => {
                            (e.currentTarget as HTMLButtonElement).style.background = 'rgba(230,57,70,0.12)';
                            (e.currentTarget as HTMLButtonElement).style.color = '#E63946';
                          }}
                          onMouseLeave={e => {
                            (e.currentTarget as HTMLButtonElement).style.background = 'transparent';
                            (e.currentTarget as HTMLButtonElement).style.color = 'rgba(230,57,70,0.45)';
                          }}
                          title="Delete post"
                        >
                          <Trash2 size={12} />
                        </button>
                      </div>
                    );
                  })}
              </div>
            )}
          </div>
        </div>

        {/* ─── Music Manager ─────────────────────────────────────────────────── */}
        <div className="mt-14">
          <div className="flex items-center gap-4 mb-8">
            <div className="h-px flex-1" style={{ background: 'rgba(56,189,248,0.15)' }} />
            <span
              className="text-xs uppercase tracking-[0.3em] font-bold"
              style={{ color: 'rgba(56,189,248,0.6)' }}
            >
              Background Music
            </span>
            <div className="h-px flex-1" style={{ background: 'rgba(56,189,248,0.15)' }} />
          </div>

          <div>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Music size={16} style={{ color: '#38BDF8' }} />
                <h3
                  className="text-lg font-bold uppercase"
                  style={{ fontFamily: "'Bangers', cursive", letterSpacing: '0.1em', color: '#38BDF8' }}
                >
                  Site Soundtrack
                </h3>
                <span
                  className="text-xs font-mono px-2 py-0.5 rounded"
                  style={{ background: 'rgba(56,189,248,0.1)', color: 'rgba(56,189,248,0.7)', border: '1px solid rgba(56,189,248,0.25)' }}
                >
                  {audioEntry ? '1 track' : 'none'}
                </span>
              </div>
              {!showAddAudio && (
                <button
                  onClick={() => setShowAddAudio(true)}
                  className="flex items-center gap-1.5 px-4 py-2 rounded text-xs font-bold uppercase transition-all"
                  style={{
                    fontFamily: "'Bangers', cursive",
                    letterSpacing: '0.1em',
                    background: 'rgba(56,189,248,0.1)',
                    border: '1px solid rgba(56,189,248,0.3)',
                    color: '#38BDF8',
                    cursor: 'pointer',
                  }}
                  onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.background = 'rgba(56,189,248,0.2)'; }}
                  onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.background = 'rgba(56,189,248,0.1)'; }}
                >
                  {audioEntry ? <><Pencil size={12} /> Replace Track</> : <><Plus size={12} /> Upload Track</>}
                </button>
              )}
            </div>

            <p className="text-xs mb-5 font-mono" style={{ color: 'rgba(255,255,255,0.3)' }}>
              Uploads a looping background music track that autoplays (muted) on the homepage. Visitors can unmute via the floating audio controls.
            </p>

            {/* Current audio entry */}
            {audioEntry && !showAddAudio && (
              <div
                className="rounded-xl p-4 flex items-center gap-4"
                style={{ border: '1px solid rgba(56,189,248,0.25)', background: 'rgba(56,189,248,0.04)', maxWidth: 520 }}
              >
                {/* Waveform icon */}
                <div
                  className="w-12 h-12 rounded-lg flex items-center justify-center shrink-0"
                  style={{ background: 'rgba(56,189,248,0.12)', border: '1px solid rgba(56,189,248,0.25)' }}
                >
                  <Music size={20} style={{ color: '#38BDF8' }} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold mb-0.5" style={{ fontFamily: "'Bangers', cursive", letterSpacing: '0.08em', color: '#38BDF8' }}>
                    {audioEntry.title ?? 'Background Music'}
                  </p>
                  <p className="text-xs font-mono truncate" style={{ color: 'rgba(255,255,255,0.3)', maxWidth: 300 }} title={audioEntry.url}>
                    {audioEntry.url}
                  </p>
                  {/* Preview player */}
                  <audio
                    controls
                    src={audioEntry.url}
                    style={{ marginTop: 8, width: '100%', maxWidth: 300, height: 28 }}
                  />
                </div>
                <button
                  onClick={async () => {
                    const ok = await deleteMedia(audioEntry.id);
                    if (ok) toast.success('Track removed');
                    else toast.error('Failed to remove track');
                  }}
                  className="flex items-center gap-1 px-2.5 py-1.5 rounded text-xs shrink-0"
                  style={{
                    background: 'rgba(230,57,70,0.08)',
                    border: '1px solid rgba(230,57,70,0.2)',
                    color: 'rgba(230,57,70,0.7)',
                    cursor: 'pointer',
                    fontFamily: "'Bangers', cursive",
                    letterSpacing: '0.06em',
                  }}
                >
                  <Trash2 size={10} />
                  Remove
                </button>
              </div>
            )}

            {/* Upload form */}
            {showAddAudio && (
              <div
                className="rounded-xl p-5"
                style={{ background: 'rgba(56,189,248,0.04)', border: '1px solid rgba(56,189,248,0.2)', maxWidth: 520 }}
              >
                <p
                  className="text-sm font-bold uppercase mb-4"
                  style={{ fontFamily: "'Bangers', cursive", letterSpacing: '0.1em', color: '#38BDF8' }}
                >
                  {audioEntry ? 'Replace Track' : 'Upload Background Track'}
                </p>

                {/* Drop zone */}
                <div className="mb-3">
                  <label className="block text-xs uppercase tracking-widest mb-1.5" style={{ color: 'rgba(255,255,255,0.4)' }}>
                    Audio File (MP3, WAV, OGG — max 100 MB)
                  </label>
                  <div
                    className="relative flex flex-col items-center justify-center gap-3 rounded-lg transition-all"
                    style={{
                      border: audioUploadedUrl
                        ? '1px solid rgba(56,189,248,0.5)'
                        : '2px dashed rgba(56,189,248,0.3)',
                      background: audioUploadedUrl
                        ? 'rgba(56,189,248,0.04)'
                        : 'rgba(56,189,248,0.03)',
                      padding: '2rem 1rem',
                      cursor: audioUploading ? 'default' : 'pointer',
                      minHeight: 110,
                    }}
                    onClick={() => !audioUploading && !audioUploadedUrl && audioFileRef.current?.click()}
                  >
                    {audioUploading ? (
                      <>
                        <div
                          className="w-8 h-8 rounded-full border-2 animate-spin"
                          style={{ borderColor: '#38BDF8', borderTopColor: 'transparent' }}
                        />
                        <p className="text-xs font-mono" style={{ color: 'rgba(255,255,255,0.5)' }}>
                          {audioUploadProgress}
                        </p>
                      </>
                    ) : audioUploadedUrl ? (
                      <>
                        <div
                          className="w-8 h-8 rounded-full flex items-center justify-center"
                          style={{ background: 'rgba(56,189,248,0.15)' }}
                        >
                          <Check size={16} style={{ color: '#38BDF8' }} />
                        </div>
                        <p className="text-xs font-bold uppercase" style={{ fontFamily: "'Bangers', cursive", letterSpacing: '0.1em', color: '#38BDF8' }}>
                          Upload Complete
                        </p>
                        <audio controls src={audioUploadedUrl} style={{ maxWidth: 280 }} />
                        <button
                          type="button"
                          onClick={e => { e.stopPropagation(); setAudioUploadedUrl(null); if (audioFileRef.current) audioFileRef.current.value = ''; }}
                          className="text-xs px-3 py-1 rounded transition-all"
                          style={{
                            background: 'rgba(255,255,255,0.06)',
                            border: '1px solid rgba(255,255,255,0.12)',
                            color: 'rgba(255,255,255,0.5)',
                            cursor: 'pointer',
                            fontFamily: 'monospace',
                          }}
                        >
                          Replace file
                        </button>
                      </>
                    ) : (
                      <>
                        <div
                          className="w-10 h-10 rounded-lg flex items-center justify-center"
                          style={{ background: 'rgba(56,189,248,0.12)', border: '1px solid rgba(56,189,248,0.25)' }}
                        >
                          <Upload size={20} style={{ color: '#38BDF8' }} />
                        </div>
                        <div className="text-center">
                          <p className="text-sm font-bold uppercase mb-1" style={{ fontFamily: "'Bangers', cursive", letterSpacing: '0.1em', color: 'rgba(255,255,255,0.7)' }}>
                            Click to select audio
                          </p>
                          <p className="text-xs" style={{ color: 'rgba(255,255,255,0.3)' }}>
                            MP3, WAV, OGG · up to 100 MB
                          </p>
                        </div>
                      </>
                    )}
                    <input
                      ref={audioFileRef}
                      type="file"
                      accept="audio/*"
                      onChange={handleAudioFileChange}
                      disabled={audioUploading}
                      style={{ display: 'none' }}
                    />
                  </div>
                </div>

                {/* Title */}
                <div className="mb-4">
                  <label className="block text-xs uppercase tracking-widest mb-1.5" style={{ color: 'rgba(255,255,255,0.4)' }}>
                    Track Title (optional)
                  </label>
                  <input
                    style={inputStyle}
                    value={audioTitle}
                    onChange={e => setAudioTitle(e.target.value)}
                    placeholder="e.g. Danger Dan! Theme"
                    disabled={audioSaving}
                  />
                </div>

                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={handleSaveAudio}
                    disabled={audioSaving || audioUploading || !audioUploadedUrl}
                    className="flex items-center gap-1.5 px-4 py-2 rounded text-xs font-bold uppercase transition-all"
                    style={{
                      fontFamily: "'Bangers', cursive",
                      letterSpacing: '0.1em',
                      background: (!audioUploadedUrl || audioSaving) ? 'rgba(56,189,248,0.06)' : 'rgba(56,189,248,0.15)',
                      border: '1px solid rgba(56,189,248,0.4)',
                      color: '#38BDF8',
                      cursor: (!audioUploadedUrl || audioSaving) ? 'not-allowed' : 'pointer',
                    }}
                  >
                    <Check size={12} />
                    {audioSaving ? 'Saving…' : 'Save Track'}
                  </button>
                  <button
                    type="button"
                    onClick={() => { setShowAddAudio(false); setAudioUploadedUrl(null); setAudioTitle(''); if (audioFileRef.current) audioFileRef.current.value = ''; }}
                    disabled={audioSaving || audioUploading}
                    className="flex items-center gap-1.5 px-4 py-2 rounded text-xs font-bold uppercase transition-all"
                    style={{
                      fontFamily: "'Bangers', cursive",
                      letterSpacing: '0.1em',
                      background: 'rgba(255,255,255,0.04)',
                      border: '1px solid rgba(255,255,255,0.1)',
                      color: 'rgba(255,255,255,0.5)',
                      cursor: 'pointer',
                    }}
                  >
                    <X size={12} /> Cancel
                  </button>
                </div>
              </div>
            )}

            {/* Empty state */}
            {!audioEntry && !showAddAudio && (
              <div
                className="flex flex-col items-center justify-center py-12 rounded-xl"
                style={{ border: '1px dashed rgba(56,189,248,0.15)', background: 'rgba(56,189,248,0.02)' }}
              >
                <Music size={24} style={{ color: 'rgba(56,189,248,0.3)', marginBottom: 8 }} />
                <p className="text-sm" style={{ color: 'rgba(255,255,255,0.3)' }}>
                  No background track yet. Upload one above.
                </p>
              </div>
            )}
          </div>
        </div>

        {/* ─── Legal Pages ────────────────────────────────────────────────── */}
        <div className="mt-14">
          <div className="flex items-center gap-4 mb-8">
            <div className="h-px flex-1" style={{ background: 'rgba(124,252,0,0.12)' }} />
            <span
              className="text-xs uppercase tracking-[0.3em] font-bold"
              style={{ color: 'rgba(124,252,0,0.5)' }}
            >
              Legal Pages
            </span>
            <div className="h-px flex-1" style={{ background: 'rgba(124,252,0,0.12)' }} />
          </div>

          {/* Tabs */}
          <div className="flex gap-2 mb-6">
            {(['terms', 'privacy'] as const).map(tab => (
              <button
                key={tab}
                type="button"
                onClick={() => setLegalTab(tab)}
                className="flex items-center gap-2 px-4 py-2 rounded text-xs font-bold uppercase transition-all"
                style={{
                  fontFamily: "'Bangers', cursive",
                  letterSpacing: '0.1em',
                  background: legalTab === tab ? 'rgba(124,252,0,0.15)' : 'rgba(255,255,255,0.04)',
                  border: legalTab === tab ? '1px solid rgba(124,252,0,0.5)' : '1px solid rgba(255,255,255,0.1)',
                  color: legalTab === tab ? '#7CFC00' : 'rgba(255,255,255,0.45)',
                  cursor: 'pointer',
                }}
              >
                <FileText size={12} />
                {tab === 'terms' ? 'Terms of Service' : 'Privacy Policy'}
              </button>
            ))}
          </div>

          {/* Terms panel */}
          {legalTab === 'terms' && (
            <div
              className="rounded-xl p-5"
              style={{ background: 'rgba(124,252,0,0.03)', border: '1px solid rgba(124,252,0,0.2)' }}
            >
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <FileText size={15} style={{ color: '#7CFC00' }} />
                  <h3
                    className="text-base font-bold uppercase"
                    style={{ fontFamily: "'Bangers', cursive", letterSpacing: '0.1em', color: '#7CFC00' }}
                  >
                    Terms of Service
                  </h3>
                </div>
                {termsPage?.updatedAt && (
                  <span className="text-xs font-mono" style={{ color: 'rgba(255,255,255,0.3)' }}>
                    Last saved: {new Date(termsPage.updatedAt * 1000).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' })}
                  </span>
                )}
              </div>
              <textarea
                value={termsContent}
                onChange={e => setTermsContent(e.target.value)}
                disabled={termsSaving}
                rows={22}
                placeholder="Enter Terms of Service content..."
                style={{
                  width: '100%',
                  background: 'rgba(0,0,0,0.4)',
                  border: '1px solid rgba(124,252,0,0.3)',
                  borderRadius: '0.375rem',
                  color: 'rgba(255,255,255,0.85)',
                  fontFamily: "'Courier New', Courier, monospace",
                  fontSize: '0.85rem',
                  lineHeight: 1.6,
                  padding: '0.875rem 1rem',
                  resize: 'vertical',
                  minHeight: 400,
                  outline: 'none',
                }}
                onFocus={e => { (e.currentTarget as HTMLTextAreaElement).style.borderColor = '#7CFC00'; }}
                onBlur={e => { (e.currentTarget as HTMLTextAreaElement).style.borderColor = 'rgba(124,252,0,0.3)'; }}
              />
              <div className="flex items-center justify-between mt-3">
                <p className="text-xs font-mono" style={{ color: 'rgba(255,255,255,0.25)' }}>
                  {termsContent.length} characters
                </p>
                <button
                  type="button"
                  onClick={() => handleSaveLegalPage('terms')}
                  disabled={termsSaving}
                  className="flex items-center gap-1.5 px-5 py-2 rounded text-sm font-bold uppercase transition-all"
                  style={{
                    fontFamily: "'Bangers', cursive",
                    letterSpacing: '0.1em',
                    background: termsSaving ? 'rgba(124,252,0,0.06)' : 'rgba(124,252,0,0.15)',
                    border: '1px solid rgba(124,252,0,0.45)',
                    color: '#7CFC00',
                    cursor: termsSaving ? 'not-allowed' : 'pointer',
                  }}
                >
                  <Check size={13} />
                  {termsSaving ? 'Saving…' : 'Save Terms'}
                </button>
              </div>
            </div>
          )}

          {/* Privacy panel */}
          {legalTab === 'privacy' && (
            <div
              className="rounded-xl p-5"
              style={{ background: 'rgba(124,252,0,0.03)', border: '1px solid rgba(124,252,0,0.2)' }}
            >
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <FileText size={15} style={{ color: '#7CFC00' }} />
                  <h3
                    className="text-base font-bold uppercase"
                    style={{ fontFamily: "'Bangers', cursive", letterSpacing: '0.1em', color: '#7CFC00' }}
                  >
                    Privacy Policy
                  </h3>
                </div>
                {privacyPage?.updatedAt && (
                  <span className="text-xs font-mono" style={{ color: 'rgba(255,255,255,0.3)' }}>
                    Last saved: {new Date(privacyPage.updatedAt * 1000).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' })}
                  </span>
                )}
              </div>
              <textarea
                value={privacyContent}
                onChange={e => setPrivacyContent(e.target.value)}
                disabled={privacySaving}
                rows={22}
                placeholder="Enter Privacy Policy content..."
                style={{
                  width: '100%',
                  background: 'rgba(0,0,0,0.4)',
                  border: '1px solid rgba(124,252,0,0.3)',
                  borderRadius: '0.375rem',
                  color: 'rgba(255,255,255,0.85)',
                  fontFamily: "'Courier New', Courier, monospace",
                  fontSize: '0.85rem',
                  lineHeight: 1.6,
                  padding: '0.875rem 1rem',
                  resize: 'vertical',
                  minHeight: 400,
                  outline: 'none',
                }}
                onFocus={e => { (e.currentTarget as HTMLTextAreaElement).style.borderColor = '#7CFC00'; }}
                onBlur={e => { (e.currentTarget as HTMLTextAreaElement).style.borderColor = 'rgba(124,252,0,0.3)'; }}
              />
              <div className="flex items-center justify-between mt-3">
                <p className="text-xs font-mono" style={{ color: 'rgba(255,255,255,0.25)' }}>
                  {privacyContent.length} characters
                </p>
                <button
                  type="button"
                  onClick={() => handleSaveLegalPage('privacy')}
                  disabled={privacySaving}
                  className="flex items-center gap-1.5 px-5 py-2 rounded text-sm font-bold uppercase transition-all"
                  style={{
                    fontFamily: "'Bangers', cursive",
                    letterSpacing: '0.1em',
                    background: privacySaving ? 'rgba(124,252,0,0.06)' : 'rgba(124,252,0,0.15)',
                    border: '1px solid rgba(124,252,0,0.45)',
                    color: '#7CFC00',
                    cursor: privacySaving ? 'not-allowed' : 'pointer',
                  }}
                >
                  <Check size={13} />
                  {privacySaving ? 'Saving…' : 'Save Policy'}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* ─── Lucky Draw Manager ─────────────────────────────────────────── */}
        <LuckyDrawManager isAdmin={isAdmin} user={user} />

        {/* ─── Fan Art Moderation ──────────────────────────────────────────── */}
        <FanArtAdmin />

        {/* ─── Danger Room Team Members ──────────────────────────────────── */}
        <div
          style={{
            margin: '32px 0 0',
            padding: '24px 0 32px',
            borderTop: '1px solid rgba(230,57,70,0.2)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
            <div
              style={{
                width: 32,
                height: 32,
                borderRadius: 8,
                background: 'rgba(230,57,70,0.12)',
                border: '1px solid rgba(230,57,70,0.3)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Users size={16} style={{ color: '#E63946' }} />
            </div>
            <div>
              <h2
                style={{
                  fontFamily: "'Bangers', cursive",
                  fontSize: '1.3rem',
                  letterSpacing: '0.1em',
                  color: '#E63946',
                  margin: 0,
                }}
              >
                DANGER ROOM TEAM MEMBERS
              </h2>
              <p style={{ color: 'rgba(255,255,255,0.35)', fontSize: '0.75rem', margin: 0, fontFamily: 'monospace' }}>
                Team members can moderate all channels and access the Core Team channel
              </p>
            </div>
          </div>

          {/* Add team member input */}
          <div style={{ display: 'flex', gap: 8, marginBottom: 20, flexWrap: 'wrap' }}>
            <input
              type="text"
              placeholder="Wallet address (e.g. Abc123...xyz)"
              value={teamMemberWallet}
              onChange={e => setTeamMemberWallet(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') handleAddTeamMember(); }}
              style={{
                flex: 1,
                minWidth: 200,
                background: 'rgba(255,255,255,0.05)',
                border: '1px solid rgba(255,255,255,0.12)',
                borderRadius: 6,
                color: 'rgba(255,255,255,0.85)',
                fontSize: '0.85rem',
                fontFamily: 'monospace',
                padding: '8px 12px',
                outline: 'none',
              }}
            />
            <button
              onClick={handleAddTeamMember}
              disabled={addingTeamMember}
              style={{
                background: addingTeamMember ? 'rgba(230,57,70,0.2)' : 'rgba(230,57,70,0.15)',
                border: '1px solid rgba(230,57,70,0.5)',
                borderRadius: 6,
                color: addingTeamMember ? 'rgba(255,255,255,0.4)' : '#E63946',
                fontFamily: "'Bangers', cursive",
                fontSize: '0.9rem',
                letterSpacing: '0.1em',
                padding: '8px 20px',
                cursor: addingTeamMember ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                transition: 'all 0.15s ease',
                whiteSpace: 'nowrap',
              }}
            >
              <Plus size={14} />
              {addingTeamMember ? 'Adding...' : 'Add Team Member'}
            </button>
          </div>

          {/* Team members list */}
          {allTeamMembers.length === 0 ? (
            <div
              style={{
                padding: '20px',
                border: '1px dashed rgba(230,57,70,0.2)',
                borderRadius: 8,
                textAlign: 'center',
                color: 'rgba(255,255,255,0.25)',
                fontFamily: 'monospace',
                fontSize: '0.8rem',
              }}
            >
              No team members yet. Add wallet addresses above.
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {allTeamMembers.map((member: TeamMembersResponse) => (
                <div
                  key={member.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    padding: '10px 14px',
                    background: 'rgba(230,57,70,0.05)',
                    border: '1px solid rgba(230,57,70,0.15)',
                    borderRadius: 6,
                  }}
                >
                  <div
                    style={{
                      width: 28,
                      height: 28,
                      borderRadius: '50%',
                      background: 'linear-gradient(135deg, #E63946 0%, #FF6B35 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '0.6rem',
                      fontWeight: 700,
                      color: '#fff',
                      flexShrink: 0,
                      fontFamily: 'monospace',
                    }}
                  >
                    {member.walletAddress.slice(0, 2).toUpperCase()}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p style={{ color: 'rgba(255,255,255,0.85)', fontSize: '0.8rem', fontFamily: 'monospace', margin: 0, wordBreak: 'break-all' }}>
                      {member.walletAddress}
                    </p>
                    <p style={{ color: 'rgba(255,255,255,0.3)', fontSize: '0.65rem', fontFamily: 'monospace', margin: '2px 0 0' }}>
                      Added {new Date(member.addedAt * 1000).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                      {' by '}
                      {member.addedBy.slice(0, 6)}...{member.addedBy.slice(-4)}
                    </p>
                  </div>
                  <button
                    onClick={() => handleRemoveTeamMember(member.id)}
                    disabled={removingTeamMember === member.id}
                    title="Remove team member"
                    style={{
                      background: 'rgba(230,57,70,0.1)',
                      border: '1px solid rgba(230,57,70,0.3)',
                      borderRadius: 4,
                      color: '#E63946',
                      cursor: removingTeamMember === member.id ? 'not-allowed' : 'pointer',
                      padding: '4px 8px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 4,
                      fontSize: '0.75rem',
                      fontFamily: 'monospace',
                      opacity: removingTeamMember === member.id ? 0.5 : 1,
                      transition: 'all 0.15s ease',
                      flexShrink: 0,
                    }}
                  >
                    <Trash2 size={12} />
                    {removingTeamMember === member.id ? '...' : 'Remove'}
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

      </div>
    </PageLayout>
  );
};

// ─── Admin Fan Art Upload Form ────────────────────────────────────────────────

const AdminFanArtUpload: React.FC = () => {
  const [artistName, setArtistName] = useState('');
  const [xProfile, setXProfile] = useState('');
  const [xLink, setXLink] = useState('');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [artType, setArtType] = useState<'human' | 'ai' | null>(null);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitPhase, setSubmitPhase] = useState<'uploading' | 'waiting' | 'saving' | null>(null);
  const [open, setOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Image too large. Max 10 MB.');
      return;
    }
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!artistName.trim()) { toast.error('Artist name is required'); return; }
    if (!imageFile) { toast.error('Please select an image'); return; }
    if (!artType) { toast.error('Please select Human Made or AI Assisted'); return; }

    setSubmitting(true);
    setSubmitPhase('uploading');

    try {
      const fileId = `fanart-admin-${crypto.randomUUID()}`;
      const path = `adminFiles/${fileId}`;
      const uploadOk = await setFile(path, imageFile);
      if (!uploadOk) throw new Error('Upload failed');

      setSubmitPhase('waiting');
      let imageUrl: string | null = null;
      for (let attempt = 0; attempt < 8; attempt++) {
        if (attempt > 0) await new Promise(r => setTimeout(r, 1000));
        const filesResult = await getFiles(path);
        const fileItem = (Array.isArray(filesResult) ? filesResult[0] : null) as { url?: string } | null;
        if (fileItem?.url) { imageUrl = fileItem.url; break; }
      }
      if (!imageUrl) throw new Error('Image URL not available after upload');

      setSubmitPhase('saving');
      const submissionId = crypto.randomUUID();
      const ok = await setFanArtSubmissions(submissionId, {
        imageUrl,
        artistName: artistName.trim(),
        xProfile: xProfile.trim() || undefined,
        xLink: xLink.trim() || undefined,
        title: title.trim() || undefined,
        description: description.trim() || undefined,
        status: 'approved',
        submittedAt: Time.Now,
      });

      if (ok) {
        toast.success('Fan art uploaded and approved!');
        setArtistName('');
        setXProfile('');
        setXLink('');
        setTitle('');
        setDescription('');
        setArtType(null);
        setImageFile(null);
        setImagePreview(null);
        setOpen(false);
      } else {
        toast.error('Failed to save fan art submission');
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Upload failed';
      toast.error(msg);
    } finally {
      setSubmitting(false);
      setSubmitPhase(null);
    }
  };

  const iStyle = {
    width: '100%',
    background: 'rgba(255,255,255,0.05)',
    border: '1px solid rgba(255,255,255,0.12)',
    color: 'rgba(255,255,255,0.9)',
    padding: '8px 10px',
    outline: 'none',
    fontSize: '0.85rem',
    borderRadius: '4px',
    fontFamily: 'monospace',
  } as const;

  return (
    <div className="mb-6">
      <button
        onClick={() => setOpen(v => !v)}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          padding: '8px 16px',
          background: open ? 'rgba(155,89,182,0.2)' : 'rgba(155,89,182,0.1)',
          border: '1px solid rgba(155,89,182,0.4)',
          color: '#9B59B6',
          fontSize: '0.8rem',
          fontFamily: "'Bangers', cursive",
          letterSpacing: '0.12em',
          cursor: 'pointer',
          borderRadius: '4px',
          transition: 'background 0.15s ease',
        }}
        onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.background = 'rgba(155,89,182,0.25)'; }}
        onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.background = open ? 'rgba(155,89,182,0.2)' : 'rgba(155,89,182,0.1)'; }}
      >
        <Upload size={13} />
        {open ? 'CLOSE UPLOAD FORM' : 'UPLOAD FAN ART (ADMIN)'}
      </button>

      {open && (
        <form
          onSubmit={handleSubmit}
          style={{
            marginTop: '12px',
            background: 'rgba(155,89,182,0.04)',
            border: '1px solid rgba(155,89,182,0.18)',
            borderRadius: '8px',
            padding: '20px',
          }}
        >
          <p style={{ fontSize: '0.72rem', color: 'rgba(155,89,182,0.6)', marginBottom: '16px', fontFamily: 'monospace', letterSpacing: '0.05em' }}>
            Uploaded fan art is automatically approved and immediately visible in the gallery.
          </p>

          {/* Image upload */}
          <div className="mb-4">
            <label style={{ display: 'block', fontSize: '0.72rem', color: 'rgba(255,255,255,0.4)', marginBottom: '6px', fontFamily: 'monospace', letterSpacing: '0.08em' }}>
              IMAGE *
            </label>
            <div
              onClick={() => !submitting && fileInputRef.current?.click()}
              style={{
                border: imagePreview ? '1px solid rgba(155,89,182,0.5)' : '2px dashed rgba(155,89,182,0.25)',
                background: 'rgba(155,89,182,0.04)',
                minHeight: '120px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: submitting ? 'default' : 'pointer',
                overflow: 'hidden',
                borderRadius: '4px',
                position: 'relative',
              }}
            >
              {imagePreview ? (
                <img src={imagePreview} alt="Preview" style={{ width: '100%', maxHeight: '220px', objectFit: 'contain', display: 'block' }} />
              ) : (
                <div style={{ textAlign: 'center', padding: '20px' }}>
                  <Upload size={22} style={{ color: 'rgba(155,89,182,0.4)', marginBottom: '8px' }} />
                  <p style={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.3)', fontFamily: 'monospace' }}>Click to select image (max 10 MB)</p>
                </div>
              )}
            </div>
            <input ref={fileInputRef} type="file" accept="image/*" onChange={handleFileChange} disabled={submitting} style={{ display: 'none' }} />
          </div>

          {/* Artist Name */}
          <div className="mb-3">
            <label style={{ display: 'block', fontSize: '0.72rem', color: 'rgba(255,255,255,0.4)', marginBottom: '4px', fontFamily: 'monospace', letterSpacing: '0.08em' }}>ARTIST NAME *</label>
            <input type="text" value={artistName} onChange={e => setArtistName(e.target.value)} placeholder="Artist name or alias..." maxLength={50} disabled={submitting} style={iStyle} />
          </div>

          {/* X Profile */}
          <div className="mb-3">
            <label style={{ display: 'block', fontSize: '0.72rem', color: 'rgba(255,255,255,0.4)', marginBottom: '4px', fontFamily: 'monospace', letterSpacing: '0.08em' }}>X / TWITTER PROFILE</label>
            <input type="text" value={xProfile} onChange={e => setXProfile(e.target.value)} placeholder="@handle" maxLength={100} disabled={submitting} style={iStyle} />
          </div>

          {/* X Link */}
          <div className="mb-3">
            <label style={{ display: 'block', fontSize: '0.72rem', color: 'rgba(255,255,255,0.4)', marginBottom: '4px', fontFamily: 'monospace', letterSpacing: '0.08em' }}>X / TWITTER LINK (optional)</label>
            <input type="url" value={xLink} onChange={e => setXLink(e.target.value)} placeholder="https://x.com/post_link" maxLength={200} disabled={submitting} style={iStyle} />
          </div>

          {/* Title */}
          <div className="mb-3">
            <label style={{ display: 'block', fontSize: '0.72rem', color: 'rgba(255,255,255,0.4)', marginBottom: '4px', fontFamily: 'monospace', letterSpacing: '0.08em' }}>TITLE (optional)</label>
            <input type="text" value={title} onChange={e => setTitle(e.target.value)} placeholder="Art title..." maxLength={80} disabled={submitting} style={iStyle} />
          </div>

          {/* Description */}
          <div className="mb-4">
            <label style={{ display: 'block', fontSize: '0.72rem', color: 'rgba(255,255,255,0.4)', marginBottom: '4px', fontFamily: 'monospace', letterSpacing: '0.08em' }}>DESCRIPTION (optional)</label>
            <textarea value={description} onChange={e => setDescription(e.target.value)} placeholder="Description..." maxLength={300} disabled={submitting} rows={2} style={{ ...iStyle, resize: 'vertical' }} />
          </div>

          {/* Art type */}
          <div className="mb-5">
            <label style={{ display: 'block', fontSize: '0.72rem', color: 'rgba(255,255,255,0.4)', marginBottom: '6px', fontFamily: 'monospace', letterSpacing: '0.08em' }}>MADE BY... *</label>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                type="button"
                onClick={() => setArtType('human')}
                style={{
                  flex: 1,
                  padding: '8px',
                  background: artType === 'human' ? 'rgba(124,252,0,0.12)' : 'rgba(255,255,255,0.03)',
                  border: artType === 'human' ? '2px solid #7CFC00' : '2px solid rgba(255,255,255,0.1)',
                  color: artType === 'human' ? '#7CFC00' : 'rgba(255,255,255,0.45)',
                  cursor: submitting ? 'not-allowed' : 'pointer',
                  fontSize: '0.78rem',
                  fontFamily: "'Bangers', cursive",
                  letterSpacing: '0.08em',
                  borderRadius: '4px',
                  transition: 'all 0.15s ease',
                }}
              >
                🎨 Human Made
              </button>
              <button
                type="button"
                onClick={() => setArtType('ai')}
                style={{
                  flex: 1,
                  padding: '8px',
                  background: artType === 'ai' ? 'rgba(56,189,248,0.1)' : 'rgba(255,255,255,0.03)',
                  border: artType === 'ai' ? '2px solid #38BDF8' : '2px solid rgba(255,255,255,0.1)',
                  color: artType === 'ai' ? '#38BDF8' : 'rgba(255,255,255,0.45)',
                  cursor: submitting ? 'not-allowed' : 'pointer',
                  fontSize: '0.78rem',
                  fontFamily: "'Bangers', cursive",
                  letterSpacing: '0.08em',
                  borderRadius: '4px',
                  transition: 'all 0.15s ease',
                }}
              >
                🤖 AI Assisted
              </button>
            </div>
          </div>

          <button
            type="submit"
            disabled={submitting || !imageFile || !artType || !artistName.trim()}
            style={{
              width: '100%',
              background: submitting || !imageFile || !artType || !artistName.trim() ? 'rgba(155,89,182,0.15)' : '#9B59B6',
              color: '#fff',
              border: '1px solid rgba(0,0,0,0.4)',
              borderRadius: '4px',
              padding: '10px',
              fontFamily: "'Bangers', cursive",
              fontSize: '1rem',
              letterSpacing: '0.12em',
              cursor: submitting || !imageFile || !artType || !artistName.trim() ? 'not-allowed' : 'pointer',
              opacity: submitting || !imageFile || !artType || !artistName.trim() ? 0.5 : 1,
              transition: 'opacity 0.15s ease',
            }}
          >
            {submitPhase === 'uploading' ? 'UPLOADING IMAGE...' : submitPhase === 'waiting' ? 'PROCESSING...' : submitPhase === 'saving' ? 'SAVING...' : 'UPLOAD & APPROVE FAN ART'}
          </button>
        </form>
      )}
    </div>
  );
};

// ─── Fan Art Admin ────────────────────────────────────────────────────────────

const FanArtAdmin: React.FC = () => {
  const { data: allSubmissions } = useRealtimeData<FanArtSubmissionsResponse[]>(
    subscribeManyFanArtSubmissions,
    true,
  );

  const [actingOn, setActingOn] = useState<string | null>(null);

  const handleApprove = async (sub: FanArtSubmissionsResponse) => {
    setActingOn(sub.id);
    const ok = await updateFanArtSubmissions(sub.id, {
      status: 'approved',
      reviewedAt: Time.Now,
    });
    if (ok) toast.success('Fan art approved!');
    else toast.error('Failed to approve');
    setActingOn(null);
  };

  const handleReject = async (sub: FanArtSubmissionsResponse) => {
    setActingOn(sub.id);
    const ok = await updateFanArtSubmissions(sub.id, {
      status: 'rejected',
      reviewedAt: Time.Now,
    });
    if (ok) toast.success('Fan art rejected');
    else toast.error('Failed to reject');
    setActingOn(null);
  };

  const handleDelete = async (sub: FanArtSubmissionsResponse) => {
    setActingOn(sub.id);
    const ok = await deleteFanArtSubmissions(sub.id);
    if (ok) toast.success('Submission deleted');
    else toast.error('Failed to delete');
    setActingOn(null);
  };

  const submissions = (allSubmissions ?? []).sort((a, b) => b.submittedAt - a.submittedAt);
  const pending = submissions.filter(s => s.status === 'pending');
  const approved = submissions.filter(s => s.status === 'approved');
  const rejected = submissions.filter(s => s.status === 'rejected');

  const statusBadge = (status: string) => {
    const colors = {
      pending: { bg: 'rgba(251,191,36,0.12)', border: 'rgba(251,191,36,0.4)', text: '#FBBF24' },
      approved: { bg: 'rgba(124,252,0,0.1)', border: 'rgba(124,252,0,0.4)', text: '#7CFC00' },
      rejected: { bg: 'rgba(230,57,70,0.1)', border: 'rgba(230,57,70,0.35)', text: '#E63946' },
    }[status] ?? { bg: 'rgba(255,255,255,0.06)', border: 'rgba(255,255,255,0.2)', text: 'rgba(255,255,255,0.6)' };
    return (
      <span
        style={{
          display: 'inline-block',
          padding: '1px 8px',
          fontFamily: "'Bangers', cursive",
          fontSize: '0.7rem',
          letterSpacing: '0.12em',
          background: colors.bg,
          border: `1px solid ${colors.border}`,
          color: colors.text,
        }}
      >
        {status.toUpperCase()}
      </span>
    );
  };

  const SubRow: React.FC<{ sub: FanArtSubmissionsResponse }> = ({ sub }) => {
    const isActing = actingOn === sub.id;
    const submittedDate = new Date(sub.submittedAt * 1000).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC',
    });

    return (
      <div
        style={{
          display: 'flex',
          gap: '12px',
          padding: '12px',
          background: 'rgba(255,255,255,0.02)',
          border: '1px solid rgba(255,255,255,0.07)',
          borderRadius: '6px',
          alignItems: 'flex-start',
          transition: 'background 0.15s ease',
        }}
        onMouseEnter={e => { (e.currentTarget as HTMLDivElement).style.background = 'rgba(255,255,255,0.04)'; }}
        onMouseLeave={e => { (e.currentTarget as HTMLDivElement).style.background = 'rgba(255,255,255,0.02)'; }}
      >
        {/* Thumbnail */}
        <div
          style={{
            width: '72px',
            height: '72px',
            flexShrink: 0,
            overflow: 'hidden',
            border: '2px solid rgba(155,89,182,0.3)',
            background: 'rgba(0,0,0,0.4)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <img
            src={sub.imageUrl}
            alt={sub.title ?? 'Fan art'}
            style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
            onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }}
          />
        </div>

        {/* Info */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', marginBottom: '4px' }}>
            <span
              style={{
                fontFamily: "'Bangers', cursive",
                fontSize: '0.9rem',
                letterSpacing: '0.06em',
                color: '#9B59B6',
              }}
            >
              {sub.artistName}
            </span>
            {sub.xLink && (
              <a
                href={sub.xLink}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  fontSize: '0.72rem',
                  color: 'rgba(29,161,242,0.8)',
                  textDecoration: 'none',
                  border: '1px solid rgba(29,161,242,0.25)',
                  padding: '1px 5px',
                  borderRadius: '3px',
                  fontFamily: 'monospace',
                }}
              >
                X link ↗
              </a>
            )}
            {statusBadge(sub.status)}
            {(sub as FanArtSubmissionsResponse & { artType?: string }).artType && (
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '3px',
                  padding: '1px 7px',
                  fontSize: '0.68rem',
                  fontFamily: "'Bangers', cursive",
                  letterSpacing: '0.08em',
                  background: (sub as FanArtSubmissionsResponse & { artType?: string }).artType === 'human'
                    ? 'rgba(124,252,0,0.1)'
                    : 'rgba(56,189,248,0.1)',
                  border: (sub as FanArtSubmissionsResponse & { artType?: string }).artType === 'human'
                    ? '1px solid rgba(124,252,0,0.4)'
                    : '1px solid rgba(56,189,248,0.35)',
                  color: (sub as FanArtSubmissionsResponse & { artType?: string }).artType === 'human'
                    ? '#7CFC00'
                    : '#38BDF8',
                  borderRadius: '2px',
                }}
              >
                {(sub as FanArtSubmissionsResponse & { artType?: string }).artType === 'human' ? '🎨 Human' : '🤖 AI'}
              </span>
            )}
          </div>
          {sub.title && (
            <p style={{ fontSize: '0.82rem', color: 'rgba(255,255,255,0.7)', marginBottom: '2px', fontWeight: 500 }}>
              {sub.title}
            </p>
          )}
          {sub.description && (
            <p style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.4)', lineHeight: 1.4 }}>
              {sub.description.slice(0, 80)}{sub.description.length > 80 ? '...' : ''}
            </p>
          )}
          <p style={{ fontSize: '0.68rem', color: 'rgba(255,255,255,0.2)', marginTop: '4px', fontFamily: 'monospace' }}>
            {submittedDate}
          </p>
        </div>

        {/* Actions */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', flexShrink: 0 }}>
          {sub.status !== 'approved' && (
            <button
              onClick={() => handleApprove(sub)}
              disabled={isActing}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                padding: '4px 10px',
                background: 'rgba(124,252,0,0.1)',
                border: '1px solid rgba(124,252,0,0.35)',
                color: '#7CFC00',
                fontSize: '0.72rem',
                fontFamily: "'Bangers', cursive",
                letterSpacing: '0.08em',
                cursor: isActing ? 'not-allowed' : 'pointer',
                opacity: isActing ? 0.5 : 1,
                transition: 'background 0.15s ease',
                whiteSpace: 'nowrap',
              }}
              onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.background = 'rgba(124,252,0,0.2)'; }}
              onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.background = 'rgba(124,252,0,0.1)'; }}
            >
              <Check size={10} />
              Approve
            </button>
          )}
          {sub.status !== 'rejected' && (
            <button
              onClick={() => handleReject(sub)}
              disabled={isActing}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                padding: '4px 10px',
                background: 'rgba(251,191,36,0.08)',
                border: '1px solid rgba(251,191,36,0.3)',
                color: '#FBBF24',
                fontSize: '0.72rem',
                fontFamily: "'Bangers', cursive",
                letterSpacing: '0.08em',
                cursor: isActing ? 'not-allowed' : 'pointer',
                opacity: isActing ? 0.5 : 1,
                transition: 'background 0.15s ease',
                whiteSpace: 'nowrap',
              }}
              onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.background = 'rgba(251,191,36,0.16)'; }}
              onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.background = 'rgba(251,191,36,0.08)'; }}
            >
              <X size={10} />
              Reject
            </button>
          )}
          <button
            onClick={() => handleDelete(sub)}
            disabled={isActing}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              padding: '4px 10px',
              background: 'rgba(230,57,70,0.08)',
              border: '1px solid rgba(230,57,70,0.2)',
              color: 'rgba(230,57,70,0.7)',
              fontSize: '0.72rem',
              fontFamily: "'Bangers', cursive",
              letterSpacing: '0.08em',
              cursor: isActing ? 'not-allowed' : 'pointer',
              opacity: isActing ? 0.5 : 1,
              transition: 'background 0.15s ease',
              whiteSpace: 'nowrap',
            }}
            onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.background = 'rgba(230,57,70,0.16)'; }}
            onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.background = 'rgba(230,57,70,0.08)'; }}
          >
            <Trash2 size={10} />
            Delete
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="mt-14">
      <div className="flex items-center gap-4 mb-8">
        <div className="h-px flex-1" style={{ background: 'rgba(155,89,182,0.18)' }} />
        <span
          className="text-xs uppercase tracking-[0.3em] font-bold"
          style={{ color: 'rgba(155,89,182,0.6)' }}
        >
          Fan Art Moderation
        </span>
        <div className="h-px flex-1" style={{ background: 'rgba(155,89,182,0.18)' }} />
      </div>

      <div className="flex items-center gap-3 mb-6">
        <Star size={16} style={{ color: '#9B59B6' }} />
        <h3
          className="text-lg font-bold uppercase"
          style={{ fontFamily: "'Bangers', cursive", letterSpacing: '0.1em', color: '#9B59B6' }}
        >
          Fan Art Submissions
        </h3>
        <span
          className="text-xs font-mono px-2 py-0.5"
          style={{ background: 'rgba(155,89,182,0.1)', color: 'rgba(155,89,182,0.7)', border: '1px solid rgba(155,89,182,0.25)' }}
        >
          {pending.length} pending · {approved.length} approved · {rejected.length} rejected
        </span>
      </div>

      <AdminFanArtUpload />

      {submissions.length === 0 ? (
        <div
          className="flex flex-col items-center justify-center py-12 rounded-xl"
          style={{ border: '1px dashed rgba(155,89,182,0.2)', background: 'rgba(155,89,182,0.03)' }}
        >
          <Star size={24} style={{ color: 'rgba(155,89,182,0.3)', marginBottom: 8 }} />
          <p className="text-sm" style={{ color: 'rgba(255,255,255,0.3)' }}>
            No fan art submissions yet.
          </p>
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          {/* Pending first */}
          {pending.length > 0 && (
            <div>
              <p
                className="text-xs uppercase tracking-widest mb-2"
                style={{ fontFamily: "'Bangers', cursive", letterSpacing: '0.15em', color: '#FBBF24' }}
              >
                Pending Review ({pending.length})
              </p>
              <div className="flex flex-col gap-2">
                {pending.map(sub => <SubRow key={sub.id} sub={sub} />)}
              </div>
            </div>
          )}
          {approved.length > 0 && (
            <div>
              <p
                className="text-xs uppercase tracking-widest mb-2 mt-4"
                style={{ fontFamily: "'Bangers', cursive", letterSpacing: '0.15em', color: '#7CFC00' }}
              >
                Approved ({approved.length})
              </p>
              <div className="flex flex-col gap-2">
                {approved.map(sub => <SubRow key={sub.id} sub={sub} />)}
              </div>
            </div>
          )}
          {rejected.length > 0 && (
            <div>
              <p
                className="text-xs uppercase tracking-widest mb-2 mt-4"
                style={{ fontFamily: "'Bangers', cursive", letterSpacing: '0.15em', color: '#E63946' }}
              >
                Rejected ({rejected.length})
              </p>
              <div className="flex flex-col gap-2">
                {rejected.map(sub => <SubRow key={sub.id} sub={sub} />)}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ─── Lucky Draw Manager ───────────────────────────────────────────────────────

const PRIZE_EMOJI: Record<string, string> = {
  freeNFT: '🖼️',
  customNFT: '✨',
  merch: '👕',
  blindBox: '📦',
};

// ─── Prize Image Upload (per-card) ───────────────────────────────────────────

interface PrizeImageUploadProps {
  prize: LuckyDrawPrizesResponse;
  user: any;
}

const PrizeImageUpload: React.FC<PrizeImageUploadProps> = ({ prize, user }) => {
  const [uploading, setUploading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(prize.imageUrl ?? null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Image too large. Max 10 MB.');
      return;
    }
    playClick();
    setUploading(true);
    try {
      const path = `adminFiles/${prize.prizeType}-prize-${Date.now()}`;
      const ok = await setFile(path, file);
      if (!ok) throw new Error('Upload returned false');
      const filesResult = await getFiles(path);
      const fileItem = (Array.isArray(filesResult) ? filesResult[0] : null) as { url?: string } | null;
      if (!fileItem?.url) throw new Error('No URL returned after upload');
      const imageUrl = fileItem.url;
      setPreviewUrl(imageUrl);

      const token = await getIdToken();
      if (!token) throw new Error('Not authenticated');
      const authApi = createAuthenticatedApiClient(token, user.address);
      await authApi.put(`/api/lucky-draw/prizes/${prize.prizeType}`, { imageUrl });
      toast.success('Image updated!');
    } catch (err: any) {
      toast.error(err?.message ?? 'Upload failed');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const currentImg = previewUrl ?? prize.imageUrl ?? null;

  return (
    <div className="flex items-center gap-2 mt-2">
      {currentImg ? (
        <img
          src={currentImg}
          alt={prize.label}
          style={{
            width: 40,
            height: 40,
            objectFit: 'contain',
            borderRadius: 4,
            border: '1px solid rgba(124,252,0,0.2)',
            background: 'rgba(0,0,0,0.3)',
            flexShrink: 0,
          }}
        />
      ) : (
        <div
          style={{
            width: 40,
            height: 40,
            borderRadius: 4,
            border: '1px dashed rgba(255,255,255,0.15)',
            background: 'rgba(255,255,255,0.03)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <span style={{ fontSize: '1rem' }}>{PRIZE_EMOJI[prize.prizeType] ?? '🎁'}</span>
        </div>
      )}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        style={{ display: 'none' }}
        onChange={handleFileChange}
        disabled={uploading}
      />
      <button
        onClick={() => { playClick(); fileInputRef.current?.click(); }}
        disabled={uploading}
        className="flex items-center gap-1 px-2 py-1 rounded text-xs transition-all"
        style={{
          background: 'rgba(255,255,255,0.04)',
          border: '1px solid rgba(255,255,255,0.12)',
          color: uploading ? 'rgba(255,255,255,0.3)' : 'rgba(255,255,255,0.6)',
          cursor: uploading ? 'not-allowed' : 'pointer',
          fontSize: '0.7rem',
        }}
      >
        <Upload size={9} />
        {uploading ? 'Uploading…' : (currentImg ? 'Change Image' : 'Upload Image')}
      </button>
    </div>
  );
};

interface LuckyDrawManagerProps {
  isAdmin: boolean;
  user: any;
}

// Helper: convert Unix seconds to datetime-local input value (local time)
function tsToDatetimeLocal(ts: number): string {
  const d = new Date(ts * 1000);
  // Format: YYYY-MM-DDTHH:mm
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

// Helper: convert datetime-local value to Unix seconds (local time)
function datetimeLocalToTs(val: string): number | null {
  if (!val) return null;
  const d = new Date(val);
  if (isNaN(d.getTime())) return null;
  return Math.floor(d.getTime() / 1000);
}

// Helper: compute prize schedule status label
function getPrizeScheduleStatus(prize: LuckyDrawPrizesResponse): { label: string; color: string } {
  const now = Math.floor(Date.now() / 1000);
  if (!prize.campaignActive) return { label: 'INACTIVE', color: 'rgba(255,255,255,0.3)' };
  if (prize.paused) return { label: 'PAUSED', color: '#FF8C00' };
  if (prize.endDate != null && now > prize.endDate) return { label: 'ENDED', color: '#E63946' };
  if (prize.startDate != null && now < prize.startDate) return { label: 'NOT STARTED', color: '#FFD700' };
  return { label: 'ACTIVE', color: '#7CFC00' };
}

const CAMPAIGN_ID = 'default';

const LuckyDrawManager: React.FC<LuckyDrawManagerProps> = ({ isAdmin, user }) => {
  const [showWinners, setShowWinners] = useState(false);
  const [resetting, setResetting] = useState<string | null>(null);
  const [toggling, setToggling] = useState<string | null>(null);
  const [seeding, setSeeding] = useState(false);
  const [removing, setRemoving] = useState<string | null>(null);
  const [addingPrize, setAddingPrize] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [addLabel, setAddLabel] = useState('');
  const [addPrizeType, setAddPrizeType] = useState('');
  const [addTotalCount, setAddTotalCount] = useState('');
  const [addImageUrl, setAddImageUrl] = useState('');
  const [addImageUploading, setAddImageUploading] = useState(false);
  const [addImagePreview, setAddImagePreview] = useState<string | null>(null);
  const addImageInputRef = useRef<HTMLInputElement>(null);
  // Schedule editing state: keyed by prizeType
  const [scheduleSaving, setScheduleSaving] = useState<string | null>(null);
  // Draft schedule values: keyed by prizeType -> { startDate: string, endDate: string }
  const [scheduleDrafts, setScheduleDrafts] = useState<Record<string, { startDate: string; endDate: string }>>({});
  const [savingRemark, setSavingRemark] = useState<string | null>(null);
  const [remarkDrafts, setRemarkDrafts] = useState<Record<string, string>>({});

  // ─── Campaign schedule state ───────────────────────────────────────────────
  const { data: campaignData } = useRealtimeData<LuckyDrawCampaignsResponse | null>(
    subscribeLuckyDrawCampaigns,
    isAdmin,
    CAMPAIGN_ID,
  );
  const [campaignStartDraft, setCampaignStartDraft] = useState('');
  const [campaignEndDraft, setCampaignEndDraft] = useState('');
  const [campaignScheduleSaving, setCampaignScheduleSaving] = useState(false);
  const [campaignTogglingActive, setCampaignTogglingActive] = useState(false);

  // Sync drafts when campaign data loads (only if user isn't editing)
  const campaignDraftSynced = useRef(false);
  useEffect(() => {
    if (campaignData && !campaignDraftSynced.current) {
      setCampaignStartDraft(tsToDatetimeLocal(campaignData.startAt));
      setCampaignEndDraft(tsToDatetimeLocal(campaignData.endAt));
      campaignDraftSynced.current = true;
    }
  }, [campaignData]);

  const { data: prizeData } = useRealtimeData<LuckyDrawPrizesResponse[]>(
    subscribeManyLuckyDrawPrizes,
    isAdmin,
  );
  const { data: entriesData } = useRealtimeData<LuckyDrawEntriesResponse[]>(
    subscribeManyLuckyDrawEntries,
    isAdmin,
  );

  const prizes = prizeData ?? [];
  const entries = entriesData ?? [];
  const winners = entries.filter(e => e.won);
  const totalDraws = entries.length;

  const winnersByPrize = useMemo(() => {
    const map: Record<string, LuckyDrawEntriesResponse[]> = {};
    winners.forEach(w => {
      if (!map[w.prizeType]) map[w.prizeType] = [];
      map[w.prizeType].push(w);
    });
    return map;
  }, [winners]);

  const handleSeedPrizes = async () => {
    setSeeding(true);
    try {
      const token = await getIdToken();
      if (!token) throw new Error('Not authenticated');
      const authApi = createAuthenticatedApiClient(token, user.address);
      const result = await authApi.post<{ results: { prizeType: string; status: string }[] }>('/api/lucky-draw/init');
      const created = result.results?.filter((r: any) => r.status === 'created').length ?? 0;
      const existing = result.results?.filter((r: any) => r.status === 'already_exists').length ?? 0;
      toast.success(`Prizes seeded: ${created} created, ${existing} already existed`);
    } catch (err: any) {
      toast.error(err?.message ?? 'Failed to seed prizes');
    } finally {
      setSeeding(false);
    }
  };

  const handleStatusChange = async (entryId: string, newStatus: string) => {
    const ok = await updateLuckyDrawEntries(entryId, { adminStatus: (newStatus || undefined) as any });
    if (ok) toast.success('Status updated');
    else toast.error('Failed');
  };

  const handleSaveRemark = async (entryId: string, status: string | undefined, remark: string | undefined) => {
    setSavingRemark(entryId);
    const ok = await updateLuckyDrawEntries(entryId, { adminStatus: status as any, adminRemark: remark as any });
    if (ok) toast.success('Saved');
    else toast.error('Failed to save');
    setSavingRemark(null);
  };

  const handleToggleCampaign = async (prize: LuckyDrawPrizesResponse) => {
    setToggling(prize.prizeType);
    try {
      const ok = await updateLuckyDrawPrizes(prize.id, { campaignActive: !prize.campaignActive });
      if (ok) {
        toast.success(`${prize.label} ${!prize.campaignActive ? 'activated' : 'deactivated'}`);
      } else {
        toast.error('Failed to update campaign status');
      }
    } catch (err: any) {
      toast.error(err?.message ?? 'Failed to update');
    } finally {
      setToggling(null);
    }
  };

  const handleResetCount = async (prize: LuckyDrawPrizesResponse) => {
    const confirmed = window.confirm(
      `Reset "${prize.label}" remaining count back to ${prize.totalCount}? This cannot be undone.`
    );
    if (!confirmed) return;
    setResetting(prize.prizeType);
    try {
      const ok = await updateLuckyDrawPrizes(prize.id, { remainingCount: prize.totalCount });
      if (ok) {
        toast.success(`${prize.label} count reset to ${prize.totalCount}`);
      } else {
        toast.error('Reset failed');
      }
    } catch (err: any) {
      toast.error(err?.message ?? 'Reset failed');
    } finally {
      setResetting(null);
    }
  };

  const handleAddImageSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Image too large. Max 10 MB.');
      return;
    }
    setAddImageUploading(true);
    try {
      const prizeTypeSlug = addPrizeType.trim() || 'new';
      const path = `adminFiles/${prizeTypeSlug}-prize-${Date.now()}`;
      const ok = await setFile(path, file);
      if (!ok) throw new Error('Upload returned false');
      const filesResult = await getFiles(path);
      const fileItem = (Array.isArray(filesResult) ? filesResult[0] : null) as { url?: string } | null;
      if (!fileItem?.url) throw new Error('No URL returned after upload');
      setAddImageUrl(fileItem.url);
      setAddImagePreview(fileItem.url);
      toast.success('Image uploaded');
    } catch (err: any) {
      toast.error(err?.message ?? 'Image upload failed');
    } finally {
      setAddImageUploading(false);
      if (addImageInputRef.current) addImageInputRef.current.value = '';
    }
  };

  const handleAddPrize = async (e: React.FormEvent) => {
    e.preventDefault();
    const count = parseInt(addTotalCount, 10);
    if (!addLabel.trim() || !addPrizeType.trim() || isNaN(count) || count < 1) {
      toast.error('Label, Prize Type ID, and Total Count (>=1) are required');
      return;
    }
    if (/\s/.test(addPrizeType)) {
      toast.error('Prize Type ID must not contain spaces');
      return;
    }
    setAddingPrize(true);
    try {
      const token = await getIdToken();
      if (!token) throw new Error('Not authenticated');
      const authApi = createAuthenticatedApiClient(token, user.address);
      await authApi.post('/api/lucky-draw/prizes', {
        prizeType: addPrizeType.trim(),
        label: addLabel.trim(),
        totalCount: count,
        ...(addImageUrl ? { imageUrl: addImageUrl } : {}),
      });
      toast.success(`Prize "${addLabel.trim()}" added!`);
      setAddLabel('');
      setAddPrizeType('');
      setAddTotalCount('');
      setAddImageUrl('');
      setAddImagePreview(null);
      setShowAddForm(false);
    } catch (err: any) {
      toast.error(err?.message ?? 'Failed to add prize');
    } finally {
      setAddingPrize(false);
    }
  };

  const handleRemovePrize = async (prize: LuckyDrawPrizesResponse) => {
    const confirmed = window.confirm(
      `Remove prize "${prize.label}" (${prize.prizeType})? This cannot be undone.`
    );
    if (!confirmed) return;
    setRemoving(prize.prizeType);
    try {
      const token = await getIdToken();
      if (!token) throw new Error('Not authenticated');
      const authApi = createAuthenticatedApiClient(token, user.address);
      await authApi.delete(`/api/lucky-draw/prizes/${prize.prizeType}`);
      toast.success(`Prize "${prize.label}" removed`);
    } catch (err: any) {
      toast.error(err?.message ?? 'Failed to remove prize');
    } finally {
      setRemoving(null);
    }
  };

  // Initialize draft for a prize if not yet set
  const getScheduleDraft = (prize: LuckyDrawPrizesResponse) => {
    if (scheduleDrafts[prize.prizeType]) return scheduleDrafts[prize.prizeType];
    return {
      startDate: prize.startDate ? tsToDatetimeLocal(prize.startDate) : '',
      endDate: prize.endDate ? tsToDatetimeLocal(prize.endDate) : '',
    };
  };

  const setScheduleDraft = (prizeType: string, field: 'startDate' | 'endDate', value: string) => {
    setScheduleDrafts(prev => ({
      ...prev,
      [prizeType]: { ...(prev[prizeType] ?? { startDate: '', endDate: '' }), [field]: value },
    }));
  };

  const handleSaveSchedule = async (prize: LuckyDrawPrizesResponse) => {
    setScheduleSaving(prize.prizeType);
    try {
      const draft = getScheduleDraft(prize);
      const startDate = datetimeLocalToTs(draft.startDate);
      const endDate = datetimeLocalToTs(draft.endDate);
      const token = await getIdToken();
      if (!token) throw new Error('Not authenticated');
      const authApi = createAuthenticatedApiClient(token, user.address);
      await authApi.put(`/api/lucky-draw/prizes/${prize.prizeType}`, {
        startDate: startDate ?? null,
        endDate: endDate ?? null,
      });
      toast.success(`Schedule saved for ${prize.label}`);
    } catch (err: any) {
      toast.error(err?.message ?? 'Failed to save schedule');
    } finally {
      setScheduleSaving(null);
    }
  };

  const handleTogglePaused = async (prize: LuckyDrawPrizesResponse) => {
    setToggling(`paused_${prize.prizeType}`);
    try {
      const token = await getIdToken();
      if (!token) throw new Error('Not authenticated');
      const authApi = createAuthenticatedApiClient(token, user.address);
      const newPaused = !prize.paused;
      await authApi.put(`/api/lucky-draw/prizes/${prize.prizeType}`, { paused: newPaused });
      toast.success(`${prize.label} ${newPaused ? 'paused' : 'unpaused'}`);
    } catch (err: any) {
      toast.error(err?.message ?? 'Failed to update paused state');
    } finally {
      setToggling(null);
    }
  };

  const handleExportWinners = () => {
    const header = 'claimedWalletAddress,twitterHandle,prizeType,drawnAt';
    const rows = winners.map(w =>
      `${w.claimedWalletAddress ?? w.walletAddress ?? ''},${w.twitterHandle},${w.prizeType},${new Date(w.drawnAt * 1000).toISOString()}`
    );
    const csv = [header, ...rows].join('\n');
    navigator.clipboard.writeText(csv).then(() => {
      toast.success(`Copied ${winners.length} winner(s) to clipboard as CSV`);
    });
  };

  const formatDate = (ts: number) => {
    const d = new Date(ts * 1000);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const truncateAddress = (addr: string | undefined) => addr ? `${addr.slice(0, 6)}…${addr.slice(-4)}` : '—';

  // ─── Campaign schedule handlers ────────────────────────────────────────────
  const handleSaveCampaignSchedule = async () => {
    const startAt = datetimeLocalToTs(campaignStartDraft);
    const endAt = datetimeLocalToTs(campaignEndDraft);
    if (!startAt) { toast.error('Start date is required'); return; }
    if (!endAt) { toast.error('End date is required'); return; }
    if (endAt <= startAt) { toast.error('End date must be after start date'); return; }
    setCampaignScheduleSaving(true);
    try {
      if (campaignData) {
        const ok = await updateLuckyDrawCampaigns(CAMPAIGN_ID, { startAt, endAt });
        if (ok) toast.success('Schedule saved');
        else toast.error('Failed to save — permission denied');
      } else {
        const ok = await setLuckyDrawCampaigns(CAMPAIGN_ID, {
          name: 'Default Campaign',
          startAt,
          endAt,
          isActive: false,
          createdAt: Time.Now,
        });
        if (ok) toast.success('Campaign created and schedule saved');
        else toast.error('Failed to create campaign — permission denied');
      }
    } catch {
      toast.error('Failed to save schedule');
    } finally {
      setCampaignScheduleSaving(false);
    }
  };

  const handleToggleCampaignActive = async () => {
    const newIsActive = !(campaignData?.isActive ?? false);
    setCampaignTogglingActive(true);
    try {
      if (campaignData) {
        const ok = await updateLuckyDrawCampaigns(CAMPAIGN_ID, { isActive: newIsActive });
        if (ok) {
          // Sync campaignActive on all prizes so the slot machine gate matches
          const allPrizes = prizeData ?? [];
          await Promise.all(
            allPrizes.map(p => updateLuckyDrawPrizes(p.id, { campaignActive: newIsActive }))
          );
          toast.success(newIsActive ? 'Slot machine is now LIVE' : 'Slot machine deactivated');
        } else {
          toast.error('Failed to update — permission denied');
        }
      } else {
        // No campaign yet: create one then activate
        const startAt = datetimeLocalToTs(campaignStartDraft) ?? Math.floor(Date.now() / 1000);
        const endAt = datetimeLocalToTs(campaignEndDraft) ?? startAt + 86400 * 7;
        const ok = await setLuckyDrawCampaigns(CAMPAIGN_ID, {
          name: 'Default Campaign',
          startAt,
          endAt,
          isActive: true,
          createdAt: Time.Now,
        });
        if (ok) {
          const allPrizes = prizeData ?? [];
          await Promise.all(allPrizes.map(p => updateLuckyDrawPrizes(p.id, { campaignActive: true })));
          toast.success('Campaign created and slot machine is now LIVE');
        } else {
          toast.error('Failed to create campaign — permission denied');
        }
      }
    } catch {
      toast.error('Failed to toggle campaign');
    } finally {
      setCampaignTogglingActive(false);
    }
  };

  if (!isAdmin) return null;

  return (
    <div className="mt-14">
      {/* Section divider */}
      <div className="flex items-center gap-4 mb-8">
        <div className="h-px flex-1" style={{ background: 'rgba(124,252,0,0.12)' }} />
        <span
          className="text-xs uppercase tracking-[0.3em] font-bold"
          style={{ color: 'rgba(124,252,0,0.5)' }}
        >
          Lucky Draw Manager
        </span>
        <div className="h-px flex-1" style={{ background: 'rgba(124,252,0,0.12)' }} />
      </div>

      <div className="flex items-center gap-2 mb-6">
        <Trophy size={16} style={{ color: '#7CFC00' }} />
        <h3
          className="text-lg font-bold uppercase"
          style={{ fontFamily: "'Bangers', cursive", letterSpacing: '0.1em', color: '#7CFC00' }}
        >
          Lucky Draw
        </h3>
        <div className="flex gap-2 ml-2">
          <span
            className="text-xs font-mono px-2 py-0.5 rounded"
            style={{ background: 'rgba(124,252,0,0.1)', color: 'rgba(124,252,0,0.7)', border: '1px solid rgba(124,252,0,0.2)' }}
          >
            {totalDraws} draws
          </span>
          <span
            className="text-xs font-mono px-2 py-0.5 rounded"
            style={{ background: 'rgba(255,215,0,0.1)', color: 'rgba(255,215,0,0.8)', border: '1px solid rgba(255,215,0,0.2)' }}
          >
            {winners.length} winners
          </span>
        </div>
        {/* Seed button */}
        <button
          onClick={handleSeedPrizes}
          disabled={seeding}
          className="ml-auto flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-bold uppercase"
          style={{
            fontFamily: "'Bangers', cursive",
            letterSpacing: '0.1em',
            background: 'rgba(124,252,0,0.08)',
            border: '1px solid rgba(124,252,0,0.25)',
            color: 'rgba(124,252,0,0.7)',
            cursor: seeding ? 'not-allowed' : 'pointer',
          }}
        >
          <Plus size={11} />
          {seeding ? 'Seeding…' : 'Seed Prizes'}
        </button>
      </div>

      {/* Prize inventory */}
      {prizes.length === 0 ? (
        <div
          className="flex flex-col items-center justify-center py-12 rounded-xl mb-6"
          style={{ border: '1px dashed rgba(124,252,0,0.15)', background: 'rgba(124,252,0,0.02)' }}
        >
          <Trophy size={24} style={{ color: 'rgba(124,252,0,0.25)', marginBottom: 8 }} />
          <p className="text-sm" style={{ color: 'rgba(255,255,255,0.3)' }}>
            No prizes seeded yet. Click "Seed Prizes" to initialize the campaign.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
          {prizes.map(prize => {
            const wonCount = winnersByPrize[prize.prizeType]?.length ?? 0;
            const isResetting = resetting === prize.prizeType;
            const isToggling = toggling === prize.prizeType;
            const pct = prize.totalCount > 0 ? prize.remainingCount / prize.totalCount : 0;

            return (
              <div
                key={prize.id}
                className="rounded-xl p-4"
                style={{
                  background: prize.campaignActive ? 'rgba(124,252,0,0.04)' : 'rgba(255,255,255,0.02)',
                  border: prize.campaignActive
                    ? '1px solid rgba(124,252,0,0.2)'
                    : '1px solid rgba(255,255,255,0.07)',
                }}
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    {prize.imageUrl ? (
                      <img
                        src={prize.imageUrl}
                        alt={prize.label}
                        style={{
                          width: 36,
                          height: 36,
                          objectFit: 'contain',
                          borderRadius: 4,
                          border: '1px solid rgba(124,252,0,0.2)',
                          background: 'rgba(0,0,0,0.3)',
                          flexShrink: 0,
                        }}
                      />
                    ) : (
                      <div
                        style={{
                          width: 36,
                          height: 36,
                          borderRadius: 4,
                          background: 'rgba(255,255,255,0.06)',
                          border: '1px solid rgba(255,255,255,0.12)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                          fontFamily: "'Bangers', cursive",
                          fontSize: '1rem',
                          color: 'rgba(255,255,255,0.35)',
                          userSelect: 'none',
                        }}
                      >
                        {(prize.label?.[0] ?? prize.prizeType?.[0] ?? '?').toUpperCase()}
                      </div>
                    )}
                    <div>
                      <div
                        className="font-bold"
                        style={{
                          fontFamily: "'Bangers', cursive",
                          letterSpacing: '0.08em',
                          color: prize.campaignActive ? '#7CFC00' : 'rgba(255,255,255,0.4)',
                          fontSize: '1rem',
                        }}
                      >
                        {prize.label}
                      </div>
                      <div
                        className="text-xs font-mono"
                        style={{ color: 'rgba(255,255,255,0.3)' }}
                      >
                        {wonCount} won · {prize.remainingCount}/{prize.totalCount} left
                      </div>
                    </div>
                  </div>
                  {/* Status badge */}
                  {(() => {
                    const status = getPrizeScheduleStatus(prize);
                    return (
                      <span
                        className="text-xs font-mono px-2 py-0.5 rounded"
                        style={{
                          background: `${status.color}18`,
                          color: status.color,
                          border: `1px solid ${status.color}44`,
                        }}
                      >
                        {status.label === 'PAUSED' ? '⏸ PAUSED'
                          : status.label === 'ENDED' ? '🔴 ENDED'
                          : status.label === 'NOT STARTED' ? '🟡 NOT STARTED'
                          : status.label === 'ACTIVE' ? '🟢 ACTIVE'
                          : status.label}
                      </span>
                    );
                  })()}
                </div>

                {/* Stock bar */}
                <div className="mb-2">
                  <div
                    style={{
                      height: '5px',
                      background: 'rgba(255,255,255,0.07)',
                      borderRadius: '2px',
                      overflow: 'hidden',
                    }}
                  >
                    <div
                      style={{
                        height: '100%',
                        width: `${pct * 100}%`,
                        background: prize.campaignActive ? '#7CFC00' : 'rgba(255,255,255,0.2)',
                        borderRadius: '2px',
                        transition: 'width 0.4s ease',
                      }}
                    />
                  </div>
                </div>

                {/* Image upload */}
                <PrizeImageUpload prize={prize} user={user} />

                {/* Schedule controls */}
                {(() => {
                  const draft = getScheduleDraft(prize);
                  const isSaving = scheduleSaving === prize.prizeType;
                  const isPausedToggling = toggling === `paused_${prize.prizeType}`;
                  return (
                    <div
                      className="mt-3 mb-3 rounded-lg p-3"
                      style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.07)' }}
                    >
                      <div
                        className="text-xs font-mono mb-2"
                        style={{ color: 'rgba(255,255,255,0.35)', letterSpacing: '0.1em' }}
                      >
                        SCHEDULE
                      </div>
                      <div className="flex flex-col gap-2 mb-2">
                        <label className="flex flex-col gap-1">
                          <span className="text-xs font-mono" style={{ color: 'rgba(255,255,255,0.4)' }}>Start Date/Time</span>
                          <input
                            type="datetime-local"
                            value={draft.startDate}
                            onChange={e => setScheduleDraft(prize.prizeType, 'startDate', e.target.value)}
                            className="text-xs rounded px-2 py-1"
                            style={{
                              background: 'rgba(255,255,255,0.05)',
                              border: '1px solid rgba(255,255,255,0.12)',
                              color: '#fff',
                              fontFamily: 'monospace',
                              outline: 'none',
                            }}
                          />
                        </label>
                        <label className="flex flex-col gap-1">
                          <span className="text-xs font-mono" style={{ color: 'rgba(255,255,255,0.4)' }}>End Date/Time</span>
                          <input
                            type="datetime-local"
                            value={draft.endDate}
                            onChange={e => setScheduleDraft(prize.prizeType, 'endDate', e.target.value)}
                            className="text-xs rounded px-2 py-1"
                            style={{
                              background: 'rgba(255,255,255,0.05)',
                              border: '1px solid rgba(255,255,255,0.12)',
                              color: '#fff',
                              fontFamily: 'monospace',
                              outline: 'none',
                            }}
                          />
                        </label>
                      </div>
                      <div className="flex gap-2 flex-wrap">
                        <button
                          onClick={() => handleSaveSchedule(prize)}
                          disabled={isSaving}
                          className="px-3 py-1 rounded text-xs font-bold"
                          style={{
                            fontFamily: "'Bangers', cursive",
                            letterSpacing: '0.08em',
                            background: 'rgba(0,191,255,0.1)',
                            border: '1px solid rgba(0,191,255,0.3)',
                            color: '#00BFFF',
                            cursor: isSaving ? 'not-allowed' : 'pointer',
                            opacity: isSaving ? 0.6 : 1,
                          }}
                        >
                          {isSaving ? 'Saving…' : 'Save Schedule'}
                        </button>
                        <button
                          onClick={() => handleTogglePaused(prize)}
                          disabled={isPausedToggling}
                          className="px-3 py-1 rounded text-xs font-bold"
                          style={{
                            fontFamily: "'Bangers', cursive",
                            letterSpacing: '0.08em',
                            background: prize.paused ? 'rgba(124,252,0,0.1)' : 'rgba(255,140,0,0.1)',
                            border: `1px solid ${prize.paused ? 'rgba(124,252,0,0.3)' : 'rgba(255,140,0,0.3)'}`,
                            color: prize.paused ? '#7CFC00' : '#FF8C00',
                            cursor: isPausedToggling ? 'not-allowed' : 'pointer',
                            opacity: isPausedToggling ? 0.6 : 1,
                          }}
                        >
                          {isPausedToggling ? '…' : prize.paused ? '▶ Unpause' : '⏸ Pause'}
                        </button>
                      </div>
                    </div>
                  );
                })()}

                {/* Action buttons */}
                <div className="flex gap-2 flex-wrap">
                  <button
                    onClick={() => handleToggleCampaign(prize)}
                    disabled={isToggling}
                    className="flex items-center gap-1 px-3 py-1.5 rounded text-xs font-bold uppercase transition-all"
                    style={{
                      fontFamily: "'Bangers', cursive",
                      letterSpacing: '0.08em',
                      background: prize.campaignActive ? 'rgba(230,57,70,0.1)' : 'rgba(124,252,0,0.1)',
                      border: `1px solid ${prize.campaignActive ? 'rgba(230,57,70,0.3)' : 'rgba(124,252,0,0.3)'}`,
                      color: prize.campaignActive ? '#E63946' : '#7CFC00',
                      cursor: isToggling ? 'not-allowed' : 'pointer',
                    }}
                  >
                    {isToggling ? '…' : (prize.campaignActive ? 'Pause' : 'Activate')}
                  </button>
                  <button
                    onClick={() => handleResetCount(prize)}
                    disabled={isResetting}
                    className="flex items-center gap-1 px-3 py-1.5 rounded text-xs transition-all"
                    style={{
                      background: 'rgba(255,255,255,0.04)',
                      border: '1px solid rgba(255,255,255,0.1)',
                      color: 'rgba(255,255,255,0.5)',
                      cursor: isResetting ? 'not-allowed' : 'pointer',
                    }}
                  >
                    <RotateCcw size={10} />
                    {isResetting ? 'Resetting…' : 'Reset Count'}
                  </button>
                  <button
                    onClick={() => handleRemovePrize(prize)}
                    disabled={removing === prize.prizeType}
                    className="flex items-center gap-1 px-3 py-1.5 rounded text-xs font-bold uppercase transition-all ml-auto"
                    style={{
                      fontFamily: "'Bangers', cursive",
                      letterSpacing: '0.08em',
                      background: 'rgba(230,57,70,0.08)',
                      border: '1px solid rgba(230,57,70,0.25)',
                      color: removing === prize.prizeType ? 'rgba(230,57,70,0.4)' : '#E63946',
                      cursor: removing === prize.prizeType ? 'not-allowed' : 'pointer',
                    }}
                  >
                    <Trash2 size={10} />
                    {removing === prize.prizeType ? 'Removing…' : 'Remove'}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ─── Campaign Schedule Controls ──────────────────────────────────── */}
      {(() => {
        const now = Math.floor(Date.now() / 1000);
        const camp = campaignData;
        const isActive = camp?.isActive ?? false;
        const isLive = isActive && camp && now >= camp.startAt && now <= camp.endAt;
        const isScheduled = isActive && camp && now < camp.startAt;
        const isEnded = isActive && camp && now > camp.endAt;

        let statusLabel = 'INACTIVE';
        let statusColor = 'rgba(255,255,255,0.3)';
        let statusGlow = '';
        if (isLive) { statusLabel = 'LIVE'; statusColor = '#7CFC00'; statusGlow = '0 0 8px rgba(124,252,0,0.6)'; }
        else if (isScheduled) { statusLabel = 'SCHEDULED'; statusColor = '#F59E0B'; }
        else if (isEnded) { statusLabel = 'ENDED'; statusColor = '#E63946'; }

        return (
          <div
            className="rounded-xl p-5 mb-6"
            style={{
              background: isLive ? 'rgba(124,252,0,0.04)' : 'rgba(255,255,255,0.02)',
              border: isLive ? '1px solid rgba(124,252,0,0.3)' : '1px solid rgba(255,255,255,0.1)',
            }}
          >
            {/* Header row */}
            <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
              <div className="flex items-center gap-2">
                <Calendar size={14} style={{ color: '#7CFC00' }} />
                <span
                  className="text-sm font-bold uppercase"
                  style={{ fontFamily: "'Bangers', cursive", letterSpacing: '0.1em', color: '#7CFC00' }}
                >
                  Slot Machine Schedule
                </span>
              </div>
              {/* Status badge */}
              <div className="flex items-center gap-3">
                <span
                  className="inline-flex items-center gap-1.5 text-xs font-mono px-2.5 py-1 rounded"
                  style={{
                    background: isLive ? 'rgba(124,252,0,0.12)' : 'rgba(255,255,255,0.06)',
                    border: `1px solid ${statusColor}30`,
                  }}
                >
                  <span
                    style={{
                      width: 7, height: 7, borderRadius: '50%',
                      background: statusColor,
                      display: 'inline-block',
                      boxShadow: statusGlow,
                    }}
                  />
                  <span style={{ color: statusColor }}>{statusLabel}</span>
                </span>
                {/* Active toggle */}
                <button
                  onClick={handleToggleCampaignActive}
                  disabled={campaignTogglingActive}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-bold uppercase transition-all"
                  style={{
                    fontFamily: "'Bangers', cursive",
                    letterSpacing: '0.08em',
                    background: isActive ? 'rgba(230,57,70,0.1)' : 'rgba(124,252,0,0.1)',
                    border: isActive ? '1px solid rgba(230,57,70,0.35)' : '1px solid rgba(124,252,0,0.35)',
                    color: isActive ? '#E63946' : '#7CFC00',
                    cursor: campaignTogglingActive ? 'not-allowed' : 'pointer',
                  }}
                >
                  {isActive ? <ToggleRight size={13} /> : <ToggleLeft size={13} />}
                  {campaignTogglingActive ? '…' : isActive ? 'Deactivate' : 'Activate'}
                </button>
              </div>
            </div>

            {/* Human-readable status line */}
            <p className="text-xs mb-4" style={{ color: isLive ? 'rgba(124,252,0,0.7)' : 'rgba(255,255,255,0.35)' }}>
              {isLive && camp
                ? `Slot machine is LIVE until ${new Date(camp.endAt * 1000).toLocaleString(undefined, { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' })}`
                : isScheduled && camp
                ? `Slot machine opens ${new Date(camp.startAt * 1000).toLocaleString(undefined, { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' })}`
                : isEnded
                ? 'Slot machine draw window has ended'
                : 'Set start & end dates, then activate to open the slot machine'}
            </p>

            {/* Date fields */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
              <div>
                <label className="block text-xs uppercase tracking-widest mb-1.5" style={{ color: 'rgba(255,255,255,0.4)' }}>
                  Start Date & Time
                </label>
                <input
                  type="datetime-local"
                  style={{
                    background: 'rgba(255,255,255,0.04)',
                    border: '1px solid rgba(255,255,255,0.14)',
                    borderRadius: '6px',
                    color: '#fff',
                    fontSize: '0.8rem',
                    padding: '7px 10px',
                    width: '100%',
                    fontFamily: 'monospace',
                  }}
                  value={campaignStartDraft}
                  onChange={e => setCampaignStartDraft(e.target.value)}
                  disabled={campaignScheduleSaving}
                />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-widest mb-1.5" style={{ color: 'rgba(255,255,255,0.4)' }}>
                  End Date & Time
                </label>
                <input
                  type="datetime-local"
                  style={{
                    background: 'rgba(255,255,255,0.04)',
                    border: '1px solid rgba(255,255,255,0.14)',
                    borderRadius: '6px',
                    color: '#fff',
                    fontSize: '0.8rem',
                    padding: '7px 10px',
                    width: '100%',
                    fontFamily: 'monospace',
                  }}
                  value={campaignEndDraft}
                  onChange={e => setCampaignEndDraft(e.target.value)}
                  disabled={campaignScheduleSaving}
                />
              </div>
            </div>

            {/* Save button */}
            <button
              onClick={handleSaveCampaignSchedule}
              disabled={campaignScheduleSaving}
              className="flex items-center gap-1.5 px-4 py-2 rounded text-xs font-bold uppercase"
              style={{
                fontFamily: "'Bangers', cursive",
                letterSpacing: '0.1em',
                background: campaignScheduleSaving ? 'rgba(124,252,0,0.06)' : 'rgba(124,252,0,0.14)',
                border: '1px solid rgba(124,252,0,0.4)',
                color: '#7CFC00',
                cursor: campaignScheduleSaving ? 'not-allowed' : 'pointer',
              }}
            >
              <Check size={12} />
              {campaignScheduleSaving ? 'Saving…' : 'Save Schedule'}
            </button>
          </div>
        );
      })()}

      {/* Add Prize form */}
      <div className="mb-6">
        <button
          onClick={() => setShowAddForm(v => !v)}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-bold uppercase mb-3"
          style={{
            fontFamily: "'Bangers', cursive",
            letterSpacing: '0.1em',
            background: showAddForm ? 'rgba(124,252,0,0.12)' : 'rgba(124,252,0,0.06)',
            border: '1px solid rgba(124,252,0,0.25)',
            color: '#7CFC00',
            cursor: 'pointer',
          }}
        >
          <Plus size={11} />
          {showAddForm ? 'Cancel' : 'Add New Prize'}
        </button>

        {showAddForm && (
          <form
            onSubmit={handleAddPrize}
            className="rounded-xl p-4 flex flex-col gap-3"
            style={{
              background: 'rgba(124,252,0,0.03)',
              border: '1px solid rgba(124,252,0,0.18)',
            }}
          >
            <div className="text-xs uppercase tracking-widest font-bold mb-1" style={{ fontFamily: "'Bangers', cursive", letterSpacing: '0.12em', color: 'rgba(124,252,0,0.6)' }}>
              New Prize
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs uppercase tracking-widest mb-1" style={{ color: 'rgba(255,255,255,0.4)' }}>
                  Prize Label
                </label>
                <input
                  style={inputStyle}
                  value={addLabel}
                  onChange={e => setAddLabel(e.target.value)}
                  placeholder="e.g. Free Mint NFT"
                  disabled={addingPrize}
                  required
                />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-widest mb-1" style={{ color: 'rgba(255,255,255,0.4)' }}>
                  Prize Type ID <span style={{ color: 'rgba(255,255,255,0.25)', fontSize: '0.65rem' }}>(no spaces)</span>
                </label>
                <input
                  style={inputStyle}
                  value={addPrizeType}
                  onChange={e => setAddPrizeType(e.target.value.replace(/\s/g, ''))}
                  placeholder="e.g. freeNFT"
                  disabled={addingPrize}
                  required
                />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-widest mb-1" style={{ color: 'rgba(255,255,255,0.4)' }}>
                  Total Count
                </label>
                <input
                  style={inputStyle}
                  type="number"
                  min="1"
                  value={addTotalCount}
                  onChange={e => setAddTotalCount(e.target.value)}
                  placeholder="e.g. 50"
                  disabled={addingPrize}
                  required
                />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-widest mb-1" style={{ color: 'rgba(255,255,255,0.4)' }}>
                  Prize Image <span style={{ color: 'rgba(255,255,255,0.25)', fontSize: '0.65rem' }}>(optional)</span>
                </label>
                <input
                  ref={addImageInputRef}
                  type="file"
                  accept="image/*"
                  style={{ display: 'none' }}
                  onChange={handleAddImageSelect}
                  disabled={addingPrize || addImageUploading}
                />
                <div className="flex items-center gap-2">
                  {addImagePreview ? (
                    <img
                      src={addImagePreview}
                      alt="preview"
                      style={{
                        width: 40,
                        height: 40,
                        objectFit: 'contain',
                        borderRadius: 4,
                        border: '1px solid rgba(124,252,0,0.25)',
                        background: 'rgba(0,0,0,0.3)',
                        flexShrink: 0,
                      }}
                    />
                  ) : (
                    <div
                      style={{
                        width: 40,
                        height: 40,
                        borderRadius: 4,
                        border: '1px dashed rgba(255,255,255,0.15)',
                        background: 'rgba(255,255,255,0.03)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      }}
                    >
                      <Image size={14} style={{ color: 'rgba(255,255,255,0.2)' }} />
                    </div>
                  )}
                  <button
                    type="button"
                    onClick={() => { playClick(); addImageInputRef.current?.click(); }}
                    disabled={addingPrize || addImageUploading}
                    className="flex items-center gap-1 px-3 py-1.5 rounded text-xs transition-all"
                    style={{
                      ...inputStyle,
                      width: 'auto',
                      padding: '0.4rem 0.75rem',
                      cursor: (addingPrize || addImageUploading) ? 'not-allowed' : 'pointer',
                      color: addImageUploading ? 'rgba(124,252,0,0.5)' : 'rgba(255,255,255,0.7)',
                    }}
                  >
                    <Upload size={11} />
                    {addImageUploading ? 'Uploading…' : addImagePreview ? 'Change' : 'Choose Image'}
                  </button>
                </div>
              </div>
            </div>
            <div>
              <button
                type="submit"
                disabled={addingPrize || addImageUploading}
                className="flex items-center gap-1.5 px-4 py-2 rounded text-xs font-bold uppercase"
                style={{
                  fontFamily: "'Bangers', cursive",
                  letterSpacing: '0.1em',
                  background: (addingPrize || addImageUploading) ? 'rgba(124,252,0,0.06)' : 'rgba(124,252,0,0.15)',
                  border: '1px solid rgba(124,252,0,0.4)',
                  color: (addingPrize || addImageUploading) ? 'rgba(124,252,0,0.4)' : '#7CFC00',
                  cursor: (addingPrize || addImageUploading) ? 'not-allowed' : 'pointer',
                }}
              >
                <Plus size={11} />
                {addingPrize ? 'Adding…' : addImageUploading ? 'Uploading Image…' : 'Add Prize'}
              </button>
            </div>
          </form>
        )}
      </div>

      {/* Winners table */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <button
            onClick={() => setShowWinners(!showWinners)}
            className="flex items-center gap-2 text-sm font-bold uppercase"
            style={{
              fontFamily: "'Bangers', cursive",
              letterSpacing: '0.1em',
              color: 'rgba(255,215,0,0.8)',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: 0,
            }}
          >
            {showWinners ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            {winners.length} Winner{winners.length !== 1 ? 's' : ''}
          </button>
          {winners.length > 0 && (
            <button
              onClick={handleExportWinners}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-bold uppercase"
              style={{
                fontFamily: "'Bangers', cursive",
                letterSpacing: '0.08em',
                background: 'rgba(255,215,0,0.08)',
                border: '1px solid rgba(255,215,0,0.2)',
                color: 'rgba(255,215,0,0.7)',
                cursor: 'pointer',
              }}
            >
              <Copy size={10} />
              Export CSV
            </button>
          )}
        </div>

        {showWinners && (
          <div
            className="rounded-xl overflow-hidden"
            style={{ border: '1px solid rgba(255,255,255,0.07)' }}
          >
            {winners.length === 0 ? (
              <div className="p-8 text-center">
                <p className="text-sm" style={{ color: 'rgba(255,255,255,0.3)' }}>
                  No winners yet.
                </p>
              </div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
                      {['Claimed Wallet', 'Twitter', 'Prize', 'Date', 'Status', 'Remark'].map(h => (
                        <th
                          key={h}
                          className="text-left px-4 py-3 text-xs uppercase tracking-widest"
                          style={{ color: 'rgba(255,255,255,0.35)', fontFamily: 'monospace', fontWeight: 400 }}
                        >
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {(winners as LuckyDrawEntriesResponseExtended[]).map((w, i) => {
                      const statusBadgeColor = (() => {
                        if (!w.adminStatus) return 'rgba(255,255,255,0.2)';
                        if (w.adminStatus === 'pending_transfer' || w.adminStatus === 'contacted') return '#F59E0B';
                        if (w.adminStatus === 'nft_transferred' || w.adminStatus === 'voucher_recorded' || w.adminStatus === 'voucher_used') return '#22C55E';
                        if (w.adminStatus === 'no_response' || w.adminStatus === 'cancelled') return '#EF4444';
                        return 'rgba(255,255,255,0.2)';
                      })();
                      const remarkDraft = w.id in remarkDrafts ? remarkDrafts[w.id] : (w.adminRemark ?? '');
                      return (
                        <tr
                          key={w.id}
                          style={{
                            borderBottom: i < winners.length - 1 ? '1px solid rgba(255,255,255,0.04)' : 'none',
                            background: i % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.015)',
                          }}
                        >
                          <td className="px-4 py-3">
                            <span className="font-mono text-xs" style={{ color: 'rgba(255,255,255,0.6)' }}>
                              {truncateAddress(w.claimedWalletAddress ?? w.walletAddress)}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <span className="font-mono text-xs" style={{ color: 'rgba(124,252,0,0.7)' }}>
                              @{w.twitterHandle}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <span
                              className="text-xs font-bold flex items-center gap-1"
                              style={{
                                fontFamily: "'Bangers', cursive",
                                letterSpacing: '0.08em',
                                color: '#fff',
                              }}
                            >
                              {(() => {
                                const p = prizes.find(p => p.prizeType === w.prizeType);
                                return p?.imageUrl ? (
                                  <img src={p.imageUrl} alt={p.label} style={{ width: 16, height: 16, objectFit: 'contain', borderRadius: 2 }} />
                                ) : (PRIZE_EMOJI[w.prizeType] ?? '🎁');
                              })()}{' '}
                              {prizes.find(p => p.prizeType === w.prizeType)?.label ?? w.prizeType}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <span className="font-mono text-xs" style={{ color: 'rgba(255,255,255,0.35)' }}>
                              {formatDate(w.drawnAt)}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              <span
                                style={{
                                  display: 'inline-block',
                                  width: 8,
                                  height: 8,
                                  borderRadius: '50%',
                                  background: statusBadgeColor,
                                  flexShrink: 0,
                                }}
                              />
                              <select
                                value={w.adminStatus ?? ''}
                                onChange={async (e) => {
                                  await handleStatusChange(w.id, e.target.value);
                                }}
                                style={{
                                  background: 'rgba(0,0,0,0.5)',
                                  border: '1px solid rgba(212,175,55,0.3)',
                                  borderRadius: 4,
                                  color: 'rgba(255,255,255,0.75)',
                                  fontSize: 11,
                                  fontFamily: 'monospace',
                                  padding: '2px 4px',
                                  cursor: 'pointer',
                                  outline: 'none',
                                }}
                              >
                                <option value="">— Pending —</option>
                                <option value="pending_transfer">⏳ Pending Transfer</option>
                                <option value="contacted">📬 Contacted</option>
                                <option value="nft_transferred">✅ NFT Transferred</option>
                                <option value="voucher_recorded">🎟 Voucher Recorded</option>
                                <option value="voucher_used">✔️ Voucher Used</option>
                                <option value="no_response">🔕 No Response</option>
                                <option value="cancelled">❌ Cancelled</option>
                              </select>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              <input
                                type="text"
                                placeholder="Add note..."
                                value={remarkDraft}
                                onChange={(e) => setRemarkDrafts(prev => ({ ...prev, [w.id]: e.target.value }))}
                                style={{
                                  background: 'rgba(0,0,0,0.4)',
                                  border: '1px solid rgba(255,255,255,0.1)',
                                  borderRadius: 4,
                                  color: 'rgba(255,255,255,0.7)',
                                  fontSize: 11,
                                  fontFamily: 'monospace',
                                  padding: '3px 6px',
                                  width: 140,
                                  outline: 'none',
                                }}
                              />
                              <button
                                onClick={() => handleSaveRemark(w.id, w.adminStatus, remarkDraft)}
                                disabled={savingRemark === w.id}
                                style={{
                                  background: 'rgba(212,175,55,0.15)',
                                  border: '1px solid rgba(212,175,55,0.3)',
                                  borderRadius: 4,
                                  color: savingRemark === w.id ? 'rgba(255,255,255,0.3)' : 'rgba(212,175,55,0.8)',
                                  fontSize: 10,
                                  fontFamily: 'monospace',
                                  padding: '3px 7px',
                                  cursor: savingRemark === w.id ? 'not-allowed' : 'pointer',
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                {savingRemark === w.id ? '...' : '💾 Save'}
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>

    </div>
  );
};

export default AdminPage;
